/**
 * Typed client for the Platform Console API (``/platform/console/v1``).
 *
 * Transport: a dedicated ``ApiClient`` instance bound to the ``/platform``
 * mount (see ``consoleApi()`` below) — ``credentials: "include"``
 * session-cookie auth, 401→refresh→retry, timeout, typed ``ApiError``.
 *
 * Mock switch: env-driven (``VITE_CONSOLE_MOCKS``) — see
 * ``CONSOLE_USE_MOCKS`` below. When on, every function short-circuits to
 * the stateful in-module mock layer (``mocks.ts``); when off, requests hit
 * the real ``/platform/console/v1`` backend.
 */

import { type ArticleGroupCatalog, type SupportCaseReplyResult, appendRunUploadFiles, getEnv } from "@ando/core";
import { ApiClient, ApiError } from "@ando/core/api";

import { getStorage } from "@/lib/storage";

import {
  ConsoleMockError,
  consoleIdentityMockStore,
  mockApproveFlowRun,
  mockConnectConnector,
  mockConnectorDetail,
  mockConnectorKinds,
  mockConnectorSyncs,
  mockCreateConversation,
  mockCreateCustomConnector,
  mockCreateFlow,
  mockCreateKey,
  mockCreateWebhook,
  mockCurateSchemaObject,
  mockDeclareConnectorAction,
  mockDeleteByok,
  mockDeleteConnectorAction,
  mockDeleteDocument,
  mockDeleteFlow,
  mockDeleteWebhook,
  mockDisableCustomConnector,
  mockDisconnectConnector,
  mockDocumentJobs,
  mockExecuteAction,
  mockFlowPalette,
  mockFlowStats,
  mockFlowTemplates,
  mockGenerateFlow,
  mockGetActionRequest,
  mockGetCustomConnector,
  mockGetFlow,
  mockGetFlowRun,
  mockGetSourceSchema,
  mockGetUks,
  mockGetWebhookDeliveries,
  mockInvokeConnectorAction,
  mockKeyAudit,
  mockListActionRequests,
  mockListConnectorActions,
  mockListConnectors,
  mockListCustomConnectors,
  mockListDocuments,
  mockListFlows,
  mockListJobs,
  mockListKeyScopes,
  mockListKeys,
  mockListModels,
  mockListSources,
  mockListWebhooks,
  mockNodeSchema,
  mockOverview,
  mockPendingApprovals,
  mockProposeAction,
  mockRejectFlowRun,
  mockRenameSource,
  mockReplayWebhookDelivery,
  mockRevertModel,
  mockRevokeKey,
  mockRunAgenticAnswer,
  mockRunFlow,
  mockRunFlowUpload,
  mockRunQuery,
  mockSetByok,
  mockSetModel,
  mockSyncConnector,
  mockTestWebhook,
  mockUpdateCustomConnector,
  mockRebindManifestPrincipal,
  mockUpdateFlow,
  mockUploadDocument,
  mockUsage,
  mockUsageBreakdown,
  mockValidateConnectorRecords,
} from "./mocks";
import { normalizeConsoleDocumentsPage, normalizeConsoleOverview } from "./normalize";
import type {
  ConsoleActionDefinition,
  ConsoleActionDefinitionCreate,
  ConsoleActionDefinitionDeleteResponse,
  ConsoleActionDefinitionsResponse,
  ConsoleActionExecuteRequest,
  ConsoleActionInvokeRequest,
  ConsoleActionInvokeResponse,
  ConsoleActionRequest,
  ConsoleActionRequestCreate,
  ConsoleActionRequestsResponse,
  ConsoleAgenticRequest,
  ConsoleAgenticResponse,
  ConsoleApiKey,
  ConsoleApproveRunEdits,
  ConsoleByokRemovedResponse,
  ConsoleConnectResponse,
  ConsoleConnectorDetail,
  ConsoleConnectorKindsResponse,
  ConsoleConnectorsResponse,
  ConsoleContextInfo,
  ConsoleConversation,
  ConsoleCreateKeyRequest,
  ConsoleCreateKeyResponse,
  ConsoleCustomConnector,
  ConsoleCustomConnectorCreate,
  ConsoleCustomConnectorDisableResponse,
  ConsoleCustomConnectorUpdate,
  ConsoleCustomConnectorsResponse,
  ConsoleDocument,
  ConsoleDocumentContent,
  ConsoleDocumentsPage,
  ConsoleEmbeddedIdentity,
  ConsoleEmbeddedCoverage,
  ConsoleDeveloperApp,
  ConsoleDeveloperApps,
  ConsoleDeveloperAppCoverage,
  ConsoleDeveloperAppUsers,
  ConsoleManagedUserConnections,
  ConsoleDeveloperTestSession,
  ConsoleDeveloperTestConnections,
  ConsoleEmbeddedIssuer,
  ConsoleEmbeddedReturnUrl,
  ConsoleEmbeddedSubject,
  ConsoleErrorEnvelope,
  ConsoleFlow,
  ConsoleFlowCreate,
  ConsoleFlowGenerateResult,
  ConsoleFlowPaletteResponse,
  ConsoleFlowRun,
  ConsoleFlowRunApproveResponse,
  ConsoleFlowRunDetail,
  ConsoleFlowRunList,
  ConsoleFlowRunRequest,
  ConsoleFlowStats,
  ConsoleFlowTemplatesResponse,
  ConsoleFlowUpdate,
  ConsoleFlowsResponse,
  ConsoleInviteResult,
  ConsoleInviteRevoked,
  ConsoleJobStatus,
  ConsoleJobsResponse,
  ConsoleKeyAuditResponse,
  ConsoleKeyRolled,
  ConsoleKeyUpdateRequest,
  ConsoleKeyUsage,
  ConsoleKeysResponse,
  ConsoleMember,
  ConsoleMemberRemoved,
  ConsoleMemberRole,
  ConsoleMembersResponse,
  ConsoleModelConfigResponse,
  ConsoleModelPurpose,
  ConsoleModelRevertResponse,
  ConsoleModelsResponse,
  ConsoleNodeSchemaResponse,
  ConsoleOverview,
  ConsolePendingApprovalsResponse,
  ConsoleQueryRequest,
  ConsoleQueryResponse,
  ConsoleQuotasResponse,
  ConsoleRecordValidateResponse,
  ConsoleRenamedSource,
  ConsoleResourcePolicy,
  ConsoleResourceAccess,
  ConsoleAccessOverviewPage,
  ConsoleIndexedKnowledgeItemsPage,
  ConsoleSelectedKnowledgePage,
  ConsoleRolesResponse,
  ConsoleSchemaObjectCuration,
  ConsoleSchemaObjectView,
  ConsoleSchemaResponse,
  ConsoleScopeInfo,
  ConsoleScopesResponse,
  ConsoleSetModelRequest,
  ConsoleSourcesResponse,
  ConsoleSyncListResponse,
  ConsoleUksResponse,
  ConsoleUsage,
  ConsoleUsageBreakdown,
  ConsoleUsageByKey,
  ConsoleUsageWindow,
  ConsoleWebhookCreate,
  ConsoleWebhookCreated,
  ConsoleWebhookDeleted,
  ConsoleWebhookDeliveriesResponse,
  ConsoleWebhookDeliveryReplayed,
  ConsoleWebhookSecretRotated,
  ConsoleWebhookSummary,
  ConsoleWebhookTestResult,
  ConsoleWebhooksResponse,
} from "./types";

/**
 * Env-driven mock switch — default OFF (real backend) in dev-with-backend.
 *
 *   - vitest (``MODE === "test"``): ALWAYS mocks — the suite exercises the
 *     stateful mock store through the typed client (same gate pattern as
 *     ``CONSOLE_ACCESS_MOCK`` in ``access.ts``).
 *   - dev/prod: mocks only when ``VITE_CONSOLE_MOCKS=true`` is set
 *     explicitly (escape hatch for working on console pages without the
 *     backend stack running).
 */
export const CONSOLE_USE_MOCKS =
  import.meta.env.MODE === "test" || import.meta.env.VITE_CONSOLE_MOCKS === "true";

const BASE = "/platform/console/v1";

const EMBEDDED_BASE = `${BASE}/embedded-connectors`;
const PERSONAL_BASE = `${BASE}/personal-connections`;
type PersonalProvider = import("./types").ConsolePersonalProvider;
type PersonalConnections = import("./types").ConsolePersonalConnections;
type PersonalSession = import("./types").ConsolePersonalSession;
type PersonalTest = import("./types").ConsolePersonalTest;
type PersonalPreview = import("./types").ConsolePersonalPreview;
type PersonalSyncResult = import("./types").ConsolePersonalSyncResult;
type PersonalSyncStatus = import("./types").ConsolePersonalSyncStatus;
type PersonalQueryResult = import("./types").ConsolePersonalQueryResult;
type PersonalSyncSettings = import("./types").ConsolePersonalSyncSettings;
type PersonalSyncPreview = import("./types").ConsolePersonalSyncPreview;
type PersonalDriveItems = import("./types").ConsolePersonalDriveItems;
type PersonalDriveSelection = import("./types").ConsolePersonalDriveSelection;
type PersonalChats = import("./types").ConsolePersonalChats;
type PersonalChatSelection = import("./types").ConsolePersonalChatSelection;
type PersonalCalendars = import("./types").ConsolePersonalCalendars;
type PersonalCalendarSelection = import("./types").ConsolePersonalCalendarSelection;

const mockPersonalSessions = new Map<string, PersonalProvider>();
const mockPersonalSyncSettings = new Map<string, PersonalSyncSettings>();
const mockPausedPersonalDrive = new Set<string>();
const mockPersonalDriveSelection = new Map<string, PersonalDriveSelection>();
const mockPersonalChatSelection = new Map<string, PersonalChatSelection>();
const mockPersonalCalendarSelection = new Map<string, PersonalCalendarSelection>();

export async function fetchConsolePersonalConnections(): Promise<PersonalConnections> {
  if (CONSOLE_USE_MOCKS) return structuredClone(consoleIdentityMockStore().personalConnections);
  return consoleApi().get<PersonalConnections>(PERSONAL_BASE);
}

export async function startConsolePersonalConnection(provider: PersonalProvider): Promise<PersonalSession> {
  if (CONSOLE_USE_MOCKS) {
    const sessionId = crypto.randomUUID();
    mockPersonalSessions.set(sessionId, provider);
    const callbackUrl = new URL("/console/accounts", window.location.origin);
    const capability = new URLSearchParams(window.location.search).get("capability");
    if (capability === "email" || capability === "drive" || capability === "calendar") callbackUrl.searchParams.set("capability", capability);
    callbackUrl.searchParams.set("ando_status", "awaiting_confirmation");
    callbackUrl.searchParams.set("ando_session", sessionId);
    callbackUrl.searchParams.set("ando_proof", "mock-proof");
    return {
      session_id: sessionId,
      authorization_url: callbackUrl.toString(),
      expires_at: new Date(Date.now() + 600_000).toISOString(),
    };
  }
  return consoleApi().post<PersonalSession>(`${PERSONAL_BASE}/sessions`, { provider });
}

export async function confirmConsolePersonalConnection(sessionId: string, proof: string): Promise<{ oauth_status: string; connection_id: string | null }> {
  if (CONSOLE_USE_MOCKS) {
    const provider = mockPersonalSessions.get(sessionId);
    if (!provider || proof !== "mock-proof") throw new Error("Conferma OAuth non valida");
    mockPersonalSessions.delete(sessionId);
    const connectionId = `mock-${sessionId}`;
    const mockStore = consoleIdentityMockStore();
    mockStore.personalConnections.connections.push({
      connection_id: connectionId, provider, account_label: `${provider}@example.com`,
      token_status: "active", grant_status: "active", expires_at: null,
    });
    return { oauth_status: "connected", connection_id: connectionId };
  }
  return consoleApi().post(`${PERSONAL_BASE}/sessions/${encodeURIComponent(sessionId)}/confirm`, { callback_proof: proof });
}

export async function syncConsolePersonalConnection(connectionId: string, pageToken?: string): Promise<PersonalSyncResult> {
  if (CONSOLE_USE_MOCKS) {
    const row = consoleIdentityMockStore().personalConnections.connections.find((item) => item.connection_id === connectionId);
    if (!row) throw new Error("Connessione non trovata");
    return { provider: row.provider, connection_id: connectionId, processed: 0, skipped: 0, next_page_token: null };
  }
  // Match the scheduled page size. A larger synchronous page can outlive the
  // browser request even while the backend continues indexing it.
  return consoleApi().post(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/sync`, { max_items: 10, ...(pageToken ? { page_token: pageToken } : {}) });
}

/** Schedule a complete scan. The worker owns pagination and resumes in the background. */
export async function startConsolePersonalSync(connectionId: string): Promise<{ status: "scheduled" | "already_running"; connection_id: string }> {
  if (CONSOLE_USE_MOCKS) return { status: "scheduled", connection_id: connectionId };
  return consoleApi().post(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/sync/start`, {});
}

export async function fetchConsolePersonalSyncSettings(connectionId: string): Promise<PersonalSyncSettings> {
  if (CONSOLE_USE_MOCKS) {
    if (!consoleIdentityMockStore().personalConnections.connections.some((item) => item.connection_id === connectionId)) throw new Error("Connessione non trovata");
    return mockPersonalSyncSettings.get(connectionId) ?? { time_range: "30d", after_date: null, before_date: null };
  }
  return consoleApi().get(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/sync-settings`);
}

export async function fetchConsolePersonalSyncPreview(connectionId: string, settings: PersonalSyncSettings): Promise<PersonalSyncPreview> {
  if (CONSOLE_USE_MOCKS) return { matching_items: null, estimated_seconds: null, estimated_storage_mb: null, status: "unavailable", unit: "email", approximate: false };
  const params = new URLSearchParams({ time_range: settings.time_range });
  if (settings.time_range === "custom") {
    if (settings.after_date) params.set("after_date", settings.after_date);
    if (settings.before_date) params.set("before_date", settings.before_date);
  }
  return consoleApi().get(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/sync-preview?${params}`);
}

export async function saveConsolePersonalSyncSettings(connectionId: string, settings: PersonalSyncSettings): Promise<PersonalSyncSettings> {
  if (CONSOLE_USE_MOCKS) {
    const row = consoleIdentityMockStore().personalConnections.connections.find((item) => item.connection_id === connectionId);
    if (!row || (row.provider !== "gmail" && row.provider !== "outlook")) throw new Error("Periodo disponibile solo per email");
    mockPersonalSyncSettings.set(connectionId, settings);
    return settings;
  }
  return consoleApi().put(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/sync-settings`, settings);
}

export async function fetchConsolePersonalSyncStatus(connectionId: string): Promise<PersonalSyncStatus> {
  if (CONSOLE_USE_MOCKS) return { status: mockPausedPersonalDrive.has(connectionId) ? "paused" : "idle", scan_active: false, indexed_documents: 0, last_success_at: null, next_due_at: null, last_error: null };
  return consoleApi().get(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/sync-status`);
}

export async function controlConsolePersonalDriveSync(connectionId: string, action: "pause" | "resume" | "force"): Promise<{ status: string }> {
  if (CONSOLE_USE_MOCKS) {
    if (action === "pause") mockPausedPersonalDrive.add(connectionId);
    else mockPausedPersonalDrive.delete(connectionId);
    return { status: action === "pause" ? "paused" : action === "resume" ? "resumed" : "scheduled" };
  }
  return consoleApi().post(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/sync/${action}`, {});
}

export async function queryConsolePersonalConnections(
  query: string,
  k = 5,
  filters?: { mode?: "retrieve" | "answer"; source?: "email" | "drive" | "chat" | "calendar"; connection_id?: string },
): Promise<PersonalQueryResult> {
  if (CONSOLE_USE_MOCKS) return { k, count: 0, results: [] };
  return consoleApi().post(`${PERSONAL_BASE}/query`, { query, k, ...filters });
}

export async function fetchConsolePersonalDriveItems(connectionId: string, folderId?: string, pageToken?: string): Promise<PersonalDriveItems> {
  if (CONSOLE_USE_MOCKS) return { items: folderId === "shared_with_me" ? [
    { id: "mock-shared-file", name: "Condiviso.pdf", kind: "file", path: "/Condivisi con me/Condiviso.pdf" },
  ] : folderId ? [] : [
    { id: "mock-folder", name: "Documenti", kind: "folder" },
    { id: "mock-file", name: "Esempio.pdf", kind: "file", mime_type: "application/pdf" },
  ], next_page_token: null };
  const params = new URLSearchParams({ page_size: "100" });
  if (folderId) params.set("folder_id", folderId);
  if (pageToken) params.set("page_token", pageToken);
  return consoleApi().get(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/drive-items?${params}`);
}

export async function searchConsolePersonalDrive(connectionId: string, query: string, pageToken?: string): Promise<PersonalDriveItems> {
  if (CONSOLE_USE_MOCKS) return { items: [
    { id: "mock-search-folder", name: "Progetti lontani", kind: "folder" as const, path: "/Archivio/Progetti lontani" },
    { id: "mock-search-file", name: "Contratto lontano.pdf", kind: "file" as const, path: "/Archivio/Contratto lontano.pdf" },
  ].filter((item) => item.name.toLocaleLowerCase().includes(query.toLocaleLowerCase())), next_page_token: null };
  const params = new URLSearchParams({ q: query, page_size: "50" });
  if (pageToken) params.set("page_token", pageToken);
  return consoleApi().get(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/drive-search?${params}`);
}

export async function fetchConsolePersonalDriveRoots(connectionId: string, pageToken?: string): Promise<PersonalDriveItems> {
  if (CONSOLE_USE_MOCKS) return { items: [
    { id: "root", name: "Il mio Drive", kind: "folder", path: "/Il mio Drive" },
    { id: "shared_with_me", name: "Condivisi con me", kind: "folder", path: "/Condivisi con me" },
    { id: "mock-shared-drive", name: "Team condiviso", kind: "folder", path: "/Team condiviso" },
  ], next_page_token: null };
  const params = new URLSearchParams({ page_size: "50" });
  if (pageToken) params.set("page_token", pageToken);
  return consoleApi().get(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/drive-roots?${params}`);
}

export async function fetchConsolePersonalDriveSelection(connectionId: string): Promise<PersonalDriveSelection> {
  if (CONSOLE_USE_MOCKS) return mockPersonalDriveSelection.get(connectionId) ?? { folder_ids: [], file_ids: [] };
  return consoleApi().get(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/drive-selection`);
}

export async function saveConsolePersonalDriveSelection(connectionId: string, selection: PersonalDriveSelection): Promise<PersonalDriveSelection> {
  if (CONSOLE_USE_MOCKS) { mockPersonalDriveSelection.set(connectionId, selection); return selection; }
  return consoleApi().put(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/drive-selection`, selection);
}

export async function fetchConsolePersonalChats(connectionId: string, pageToken?: string): Promise<PersonalChats> {
  if (CONSOLE_USE_MOCKS) return { items: [{ id: "mock-chat", name: "Chat di prova", kind: "chat" }], next_page_token: null };
  const params = new URLSearchParams({ page_size: "50" });
  if (pageToken) params.set("page_token", pageToken);
  return consoleApi().get(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/chats?${params}`);
}

export async function fetchConsolePersonalChatSelection(connectionId: string): Promise<PersonalChatSelection> {
  if (CONSOLE_USE_MOCKS) return mockPersonalChatSelection.get(connectionId) ?? { chat_ids: [] };
  return consoleApi().get(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/chat-selection`);
}

export async function saveConsolePersonalChatSelection(connectionId: string, selection: PersonalChatSelection): Promise<PersonalChatSelection> {
  if (CONSOLE_USE_MOCKS) { mockPersonalChatSelection.set(connectionId, selection); return selection; }
  return consoleApi().put(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/chat-selection`, selection);
}

export async function fetchConsolePersonalCalendars(connectionId: string): Promise<PersonalCalendars> {
  if (CONSOLE_USE_MOCKS) return { provider: "google_calendar", items: [{ id: "primary", name: "Calendario principale", is_primary: true }] };
  return consoleApi().get(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/calendars`);
}

export async function fetchConsolePersonalCalendarSelection(connectionId: string): Promise<PersonalCalendarSelection> {
  if (CONSOLE_USE_MOCKS) return mockPersonalCalendarSelection.get(connectionId) ?? { calendar_ids: [], past_days: 30, future_days: 180 };
  return consoleApi().get(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/calendar-selection`);
}

export async function saveConsolePersonalCalendarSelection(connectionId: string, selection: PersonalCalendarSelection): Promise<PersonalCalendarSelection> {
  if (CONSOLE_USE_MOCKS) { mockPersonalCalendarSelection.set(connectionId, selection); return selection; }
  return consoleApi().put(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/calendar-selection`, selection);
}

export async function testConsolePersonalConnection(connectionId: string): Promise<PersonalTest> {
  if (CONSOLE_USE_MOCKS) {
    const row = consoleIdentityMockStore().personalConnections.connections.find((item) => item.connection_id === connectionId);
    if (!row) throw new Error("Connessione non trovata");
    return { status: "healthy", provider: row.provider, account_label: row.account_label };
  }
  return consoleApi().post<PersonalTest>(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/test`, {});
}

export async function previewConsolePersonalConnection(connectionId: string): Promise<PersonalPreview> {
  if (CONSOLE_USE_MOCKS) {
    const row = consoleIdentityMockStore().personalConnections.connections.find((item) => item.connection_id === connectionId);
    if (!row) throw new Error("Connessione non trovata");
    return { provider: row.provider, items: [] };
  }
  return consoleApi().get<PersonalPreview>(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/preview`);
}

export async function disconnectConsolePersonalConnection(connectionId: string): Promise<void> {
  if (CONSOLE_USE_MOCKS) {
    const mockStore = consoleIdentityMockStore();
    mockStore.personalConnections = { connections: mockStore.personalConnections.connections.filter((row) => row.connection_id !== connectionId) };
    return;
  }
  await consoleApi().delete<unknown>(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}`);
}
const mockEmbeddedIdentity = () => consoleIdentityMockStore().embeddedIdentity;

export async function fetchConsoleEmbeddedIdentity(): Promise<ConsoleEmbeddedIdentity> {
  if (CONSOLE_USE_MOCKS) return structuredClone(mockEmbeddedIdentity());
  return consoleApi().get<ConsoleEmbeddedIdentity>(`${EMBEDDED_BASE}/identity`);
}

export async function fetchConsoleEmbeddedCoverage(): Promise<ConsoleEmbeddedCoverage> {
  if (CONSOLE_USE_MOCKS) return {
    issuers: mockEmbeddedIdentity().issuers.map((row) => ({
      issuer: row.issuer,
      enabled: row.enabled,
      active_subjects: mockEmbeddedIdentity().subjects.filter((subject) =>
        subject.issuer === row.issuer && subject.status === "active").length,
      connected_users: 0,
      active_connections: 0,
      needs_reauth: 0,
    })),
  };
  return consoleApi().get<ConsoleEmbeddedCoverage>(`${EMBEDDED_BASE}/coverage`);
}

export async function fetchConsoleDeveloperApps(): Promise<ConsoleDeveloperApps> {
  if (CONSOLE_USE_MOCKS) return structuredClone(consoleIdentityMockStore().developerApps);
  return consoleApi().get<ConsoleDeveloperApps>(`${EMBEDDED_BASE}/apps`);
}

export async function createConsoleDeveloperApp(
  name: string,
  identityMode: "server" | "oidc" | "managed" = "server",
  identityIssuer?: string,
  identityAudience?: string,
  loginRedirectUri?: string,
): Promise<ConsoleDeveloperApp> {
  if (CONSOLE_USE_MOCKS) {
    const app: ConsoleDeveloperApp = {
      id: crypto.randomUUID(), name, status: "active",
      identity_mode: identityMode, identity_issuer: identityIssuer ?? null, identity_audience: identityAudience ?? null,
      login_redirect_uri: loginRedirectUri ?? null,
      created_at: new Date().toISOString(), return_urls: [],
    };
    consoleIdentityMockStore().developerApps.apps.push(app);
    return structuredClone(app);
  }
  return consoleApi().post<ConsoleDeveloperApp>(`${EMBEDDED_BASE}/apps`, {
    name, identity_mode: identityMode, identity_issuer: identityIssuer ?? null, identity_audience: identityAudience ?? null,
    login_redirect_uri: loginRedirectUri ?? null,
  });
}

export async function reconcileConsoleDeveloperApp(appId: string): Promise<{ status: "ready" }> {
  if (CONSOLE_USE_MOCKS) {
    const app = consoleIdentityMockStore().developerApps.apps.find((row) => row.id === appId);
    if (!app) throw new Error("Applicazione non trovata");
    app.managed_status = "ready";
    return { status: "ready" };
  }
  return consoleApi().post<{ status: "ready" }>(`${EMBEDDED_BASE}/apps/${encodeURIComponent(appId)}/reconcile`, {});
}

export async function deactivateConsoleDeveloperApp(appId: string): Promise<{ status: "disabled" }> {
  if (CONSOLE_USE_MOCKS) {
    const app = consoleIdentityMockStore().developerApps.apps.find((row) => row.id === appId);
    if (!app) throw new Error("Applicazione non trovata");
    app.status = "disabled";
    return { status: "disabled" };
  }
  return consoleApi().post<{ status: "disabled" }>(`${EMBEDDED_BASE}/apps/${encodeURIComponent(appId)}/deactivate`, {});
}

export async function addConsoleDeveloperAppReturnUrl(
  appId: string, url: string,
): Promise<{ url: string; active: boolean }> {
  if (CONSOLE_USE_MOCKS) {
    const app = consoleIdentityMockStore().developerApps.apps.find((row) => row.id === appId);
    if (!app) throw new Error("Applicazione non trovata");
    const entry = { url, active: true };
    app.return_urls.push(entry);
    return entry;
  }
  return consoleApi().put<{ url: string; active: boolean }>(
    `${EMBEDDED_BASE}/apps/${encodeURIComponent(appId)}/return-urls`, { url },
  );
}

export async function fetchConsoleDeveloperAppCoverage(appId: string): Promise<ConsoleDeveloperAppCoverage> {
  if (CONSOLE_USE_MOCKS) return {
    active_users: 0, active_connections: 0, needs_reauth: 0, sync_errors: 0,
  };
  return consoleApi().get<ConsoleDeveloperAppCoverage>(
    `${EMBEDDED_BASE}/apps/${encodeURIComponent(appId)}/coverage`,
  );
}

export async function fetchConsoleDeveloperAppUsers(appId: string, offset = 0, limit = 20, q?: string): Promise<ConsoleDeveloperAppUsers> {
  if (CONSOLE_USE_MOCKS) return { users: [], total: 0, limit, offset };
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (q?.trim()) params.set("q", q.trim());
  return consoleApi().get<ConsoleDeveloperAppUsers>(
    `${EMBEDDED_BASE}/apps/${encodeURIComponent(appId)}/users?${params.toString()}`,
  );
}

export async function revokeConsoleDeveloperAppUser(appId: string, userId: string): Promise<void> {
  if (CONSOLE_USE_MOCKS) return;
  await consoleApi().post(
    `${EMBEDDED_BASE}/apps/${encodeURIComponent(appId)}/users/${encodeURIComponent(userId)}/revoke`,
    {},
  );
}

export async function fetchConsoleManagedUserConnections(source: "app" | "issuer", sourceId: string, userId: string): Promise<ConsoleManagedUserConnections> {
  if (CONSOLE_USE_MOCKS) return { connections: [] };
  const path = source === "app"
    ? `${EMBEDDED_BASE}/apps/${encodeURIComponent(sourceId)}/users/${encodeURIComponent(userId)}/connections`
    : `${EMBEDDED_BASE}/issuers/users/${encodeURIComponent(userId)}/connections?${new URLSearchParams({ issuer: sourceId })}`;
  return consoleApi().get<ConsoleManagedUserConnections>(path);
}

export async function revokeConsoleManagedUserConnection(source: "app" | "issuer", sourceId: string, userId: string, connectionId: string): Promise<void> {
  if (CONSOLE_USE_MOCKS) return;
  const path = source === "app"
    ? `${EMBEDDED_BASE}/apps/${encodeURIComponent(sourceId)}/users/${encodeURIComponent(userId)}/connections/${encodeURIComponent(connectionId)}/revoke`
    : `${EMBEDDED_BASE}/issuers/users/${encodeURIComponent(userId)}/connections/${encodeURIComponent(connectionId)}/revoke?${new URLSearchParams({ issuer: sourceId })}`;
  await consoleApi().post(path, {});
}

export async function fetchConsoleOidcUsers(issuer: string, offset = 0, limit = 20): Promise<ConsoleDeveloperAppUsers> {
  if (CONSOLE_USE_MOCKS) return { users: [], total: 0, limit, offset };
  const params = new URLSearchParams({ issuer, limit: String(limit), offset: String(offset) });
  return consoleApi().get<ConsoleDeveloperAppUsers>(`${EMBEDDED_BASE}/issuers/users?${params}`);
}

export async function revokeConsoleOidcUser(issuer: string, userId: string): Promise<void> {
  if (CONSOLE_USE_MOCKS) return;
  const params = new URLSearchParams({ issuer });
  await consoleApi().post(
    `${EMBEDDED_BASE}/issuers/users/${encodeURIComponent(userId)}/revoke?${params}`,
    {},
  );
}

function developerTestPath(appId: string): string {
  return `${EMBEDDED_BASE}/apps/${encodeURIComponent(appId)}/test`;
}

const mockDeveloperTestConnections = new Map<string, ConsoleDeveloperTestConnections>();

export async function startConsoleDeveloperTest(appId: string, provider: string): Promise<ConsoleDeveloperTestSession> {
  if (CONSOLE_USE_MOCKS) return {
    session_id: `mock-${provider}`, authorization_url: `${window.location.origin}/console/embedded-identity?app_id=${encodeURIComponent(appId)}&ando_session=mock-${provider}&ando_status=awaiting_confirmation&ando_proof=mock-proof`,
    expires_at: new Date(Date.now() + 600_000).toISOString(),
  };
  return consoleApi().post<ConsoleDeveloperTestSession>(`${developerTestPath(appId)}/connection-sessions`, { provider });
}

export async function confirmConsoleDeveloperTest(appId: string, sessionId: string, proof: string): Promise<{ oauth_status: string; connection_id: string | null }> {
  if (CONSOLE_USE_MOCKS) {
    if (!sessionId.startsWith("mock-") || proof !== "mock-proof") throw new Error("Prova OAuth non valida");
    const connectionId = `mock-${appId}-${sessionId.slice(5)}`;
    const store = mockDeveloperTestConnections.get(appId) ?? { connections: [] };
    if (!store.connections.some((row) => row.connection_id === connectionId)) store.connections.push({
      connection_id: connectionId, provider: sessionId.slice(5), account_label: `${sessionId.slice(5)}@example.com`,
      token_status: "active", grant_status: "active", expires_at: new Date(Date.now() + 3_600_000).toISOString(),
    });
    mockDeveloperTestConnections.set(appId, store);
    return { oauth_status: "connected", connection_id: connectionId };
  }
  return consoleApi().post(`${developerTestPath(appId)}/connection-sessions/${encodeURIComponent(sessionId)}/confirm`, { callback_proof: proof });
}

export async function fetchConsoleDeveloperTestConnections(appId: string): Promise<ConsoleDeveloperTestConnections> {
  if (CONSOLE_USE_MOCKS) return structuredClone(mockDeveloperTestConnections.get(appId) ?? { connections: [] });
  return consoleApi().get<ConsoleDeveloperTestConnections>(`${developerTestPath(appId)}/connections`);
}

export async function testConsoleDeveloperConnection(appId: string, connectionId: string): Promise<{ status: string; account_label: string }> {
  if (CONSOLE_USE_MOCKS) return { status: "healthy", account_label: mockDeveloperTestConnections.get(appId)?.connections.find((row) => row.connection_id === connectionId)?.account_label ?? "" };
  return consoleApi().get(`${developerTestPath(appId)}/connections/${encodeURIComponent(connectionId)}/test`);
}

export async function previewConsoleDeveloperConnection(appId: string, connectionId: string): Promise<PersonalPreview> {
  if (CONSOLE_USE_MOCKS) return { provider: mockDeveloperTestConnections.get(appId)?.connections.find((row) => row.connection_id === connectionId)?.provider as PersonalProvider ?? "gmail", items: [] };
  return consoleApi().get(`${developerTestPath(appId)}/connections/${encodeURIComponent(connectionId)}/preview`);
}

export async function syncConsoleDeveloperConnection(appId: string, connectionId: string): Promise<{ processed: number; skipped: number; next_page_token: string | null }> {
  if (CONSOLE_USE_MOCKS) return { processed: 0, skipped: 0, next_page_token: null };
  return consoleApi().post(`${developerTestPath(appId)}/connections/${encodeURIComponent(connectionId)}/sync`, { max_items: 50 });
}

export async function startConsoleDeveloperSync(appId: string, connectionId: string): Promise<{ status: "scheduled" | "already_running"; connection_id: string }> {
  if (CONSOLE_USE_MOCKS) return { status: "scheduled", connection_id: connectionId };
  return consoleApi().post(`${developerTestPath(appId)}/connections/${encodeURIComponent(connectionId)}/sync/start`, {});
}

export async function queryConsoleDeveloperTest(appId: string, query: string): Promise<{ results: Array<{ text?: string; title?: string }> }> {
  if (CONSOLE_USE_MOCKS) return { results: [] };
  return consoleApi().post(`${developerTestPath(appId)}/query`, { query, k: 5 });
}

export async function revokeConsoleDeveloperConnection(appId: string, connectionId: string): Promise<void> {
  if (CONSOLE_USE_MOCKS) {
    const store = mockDeveloperTestConnections.get(appId);
    if (store) store.connections = store.connections.filter((row) => row.connection_id !== connectionId);
    return;
  }
  await consoleApi().delete(`${developerTestPath(appId)}/connections/${encodeURIComponent(connectionId)}`);
}

export async function saveConsoleEmbeddedIssuer(input: ConsoleEmbeddedIssuer): Promise<ConsoleEmbeddedIssuer> {
  if (CONSOLE_USE_MOCKS) {
    mockEmbeddedIdentity().issuers = [...mockEmbeddedIdentity().issuers.filter((row) => row.issuer !== input.issuer), input];
    return input;
  }
  return consoleApi().put<ConsoleEmbeddedIssuer>(`${EMBEDDED_BASE}/issuers`, input);
}

export async function testConsoleEmbeddedIssuer(input: Pick<ConsoleEmbeddedIssuer, "issuer" | "audience" | "jwks_uri">): Promise<{ status: string }> {
  if (CONSOLE_USE_MOCKS) return { status: "healthy" };
  return consoleApi().post<{ status: string }>(`${EMBEDDED_BASE}/issuers/test`, input);
}

export async function saveConsoleEmbeddedSubject(input: Omit<ConsoleEmbeddedSubject, "status">): Promise<ConsoleEmbeddedSubject> {
  if (CONSOLE_USE_MOCKS) {
    const row: ConsoleEmbeddedSubject = { ...input, status: "active" };
    mockEmbeddedIdentity().subjects = [...mockEmbeddedIdentity().subjects.filter(
      (item) => item.issuer !== input.issuer || item.subject !== input.subject,
    ), row];
    return row;
  }
  return consoleApi().put<ConsoleEmbeddedSubject>(`${EMBEDDED_BASE}/subjects`, input);
}

export async function saveConsoleEmbeddedReturnUrl(input: Pick<ConsoleEmbeddedReturnUrl, "issuer" | "url">): Promise<ConsoleEmbeddedReturnUrl> {
  if (CONSOLE_USE_MOCKS) {
    const row: ConsoleEmbeddedReturnUrl = { ...input, active: true };
    mockEmbeddedIdentity().return_urls = [...mockEmbeddedIdentity().return_urls.filter(
      (item) => item.issuer !== input.issuer || item.url !== input.url,
    ), row];
    return row;
  }
  return consoleApi().put<ConsoleEmbeddedReturnUrl>(`${EMBEDDED_BASE}/return-urls`, input);
}

async function disableEmbedded<T>(path: string, body: object, result: T): Promise<T> {
  if (CONSOLE_USE_MOCKS) return result;
  return consoleApi().delete<T>(`${EMBEDDED_BASE}/${path}`, {
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function disableConsoleEmbeddedIssuer(issuer: string): Promise<void> {
  await disableEmbedded("issuers", { issuer }, { disabled: true });
  if (CONSOLE_USE_MOCKS) mockEmbeddedIdentity().issuers = mockEmbeddedIdentity().issuers.map(
    (row) => row.issuer === issuer ? { ...row, enabled: false } : row,
  );
}

export async function revokeConsoleEmbeddedSubject(input: Omit<ConsoleEmbeddedSubject, "status">): Promise<void> {
  await disableEmbedded("subjects", input, { revoked: true });
  if (CONSOLE_USE_MOCKS) mockEmbeddedIdentity().subjects = mockEmbeddedIdentity().subjects.map(
    (row) => row.issuer === input.issuer && row.subject === input.subject && row.user_id === input.user_id
      ? { ...row, status: "revoked" } : row,
  );
}

export async function disableConsoleEmbeddedReturnUrl(input: Pick<ConsoleEmbeddedReturnUrl, "issuer" | "url">): Promise<void> {
  await disableEmbedded("return-urls", input, { disabled: true });
  if (CONSOLE_USE_MOCKS) mockEmbeddedIdentity().return_urls = mockEmbeddedIdentity().return_urls.map(
    (row) => row.issuer === input.issuer && row.url === input.url ? { ...row, active: false } : row,
  );
}

/**
 * Dedicated transport for the console — same ``ApiClient`` machinery as the
 * product singleton (``credentials: "include"``, 401→refresh→retry, typed
 * ``ApiError``) but bound to the ``/platform`` mount instead of ``/api/v1``:
 * the product ``apiClient`` prefixes ``getEnv().apiUrl`` (BFF ``/api/v1``
 * base), which would mangle console paths into ``/api/v1/platform/...``.
 *
 *   - Web: RELATIVE base ("") — requests go same-origin and the Vite dev
 *     proxy (``vite.config.ts::server.proxy["/platform"]``) forwards them to
 *     the FastAPI backend, exactly like ``/api/v1`` + ``/ws``. Session
 *     cookies (host-only ``localhost``) ride automatically.
 *   - Tauri: no proxy in front of the WebView — derive the absolute backend
 *     origin by stripping the ``/api/v1`` suffix from ``apiUrl``.
 *
 * Lazily constructed so ``getEnv()`` runs after ``runtime-config.js``
 * injection, not at module load.
 */
let consoleApiClient: ApiClient | null = null;

/** Shared session-authenticated transport for Console feature slices. */
export function consoleApi(): ApiClient {
  if (!consoleApiClient) {
    const { apiUrl, isTauri } = getEnv();
    const baseUrl = isTauri ? apiUrl.replace(/\/api\/v1\/?$/, "") : "";
    consoleApiClient = new ApiClient({ baseUrl });
  }
  return consoleApiClient;
}

// ── Error handling ────────────────────────────────────────────────────────

function isConsoleErrorEnvelope(data: unknown): data is ConsoleErrorEnvelope {
  if (typeof data !== "object" || data === null) return false;
  const { error } = data as { error?: unknown };
  if (typeof error !== "object" || error === null) return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  return typeof code === "string" && typeof message === "string";
}

/**
 * Extract a human-readable message from any console-area failure —
 * understands the ``{"error":{code,message}}`` envelope on ``ApiError``
 * bodies, the mock-layer error, and plain ``Error``s.
 */
export function getConsoleErrorMessage(err: unknown): string {
  if (err instanceof ConsoleMockError) return err.message;
  if (err instanceof ApiError && isConsoleErrorEnvelope(err.data)) {
    return err.data.error.message;
  }
  if (err instanceof Error && err.message.length > 0) return err.message;
  return "Errore inatteso. Riprova.";
}

// ── Overview ──────────────────────────────────────────────────────────────

export async function fetchConsoleOverview(): Promise<ConsoleOverview> {
  if (CONSOLE_USE_MOCKS) return normalizeConsoleOverview(await mockOverview());
  // ``unknown`` on purpose: the real wire shape drifts from the UI model
  // (nested ``documents``, ``job_id``/``filename``, ``provider``/…) — the
  // normalizer maps every known variant and never throws.
  return normalizeConsoleOverview(await consoleApi().get<unknown>(`${BASE}/overview`));
}

// ── Console context (caller identity + capability tier) ─────────────────────

/**
 * ``GET /context`` — the caller's tenant + ORG role + management capability.
 * The in-console section gate reads ``can_manage`` (org_role ∈ {owner, admin})
 * to decide the Account group's visibility. The mock resolves an owner so the
 * mock-driven suite sees the full section set.
 */
export async function fetchConsoleContext(): Promise<ConsoleContextInfo> {
  if (CONSOLE_USE_MOCKS) return { tenant_id: "org_demo", org_role: "owner", can_manage: true };
  return consoleApi().get<ConsoleContextInfo>(`${BASE}/context`);
}

// ── Invoices (platform-usage collection history) ────────────────────────────

/** One platform-usage invoice (Stripe collection doc, not an SDI fattura). */
export interface ConsoleInvoice {
  id: string;
  number: string | null;
  status: string | null;
  currency: string | null;
  amount_due: number | null;
  amount_paid: number | null;
  /** Billed period, ``YYYY-MM``. */
  period: string | null;
  /** Unix timestamp. */
  created: number | null;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

export async function fetchConsoleInvoices(): Promise<ConsoleInvoice[]> {
  if (CONSOLE_USE_MOCKS) return [];
  const res = await consoleApi().get<{ invoices: ConsoleInvoice[] }>(`${BASE}/invoices`);
  return res.invoices ?? [];
}

// ── Prepaid credits ─────────────────────────────────────────────────────────

export interface ConsoleCreditGrant {
  id: string;
  amount_cents: number;
  kind: string;
  note: string | null;
  created_at: string;
}

export interface ConsoleCredits {
  is_prepaid: boolean;
  balance_cents: number;
  granted_cents: number;
  consumed_cents: number;
  grants: ConsoleCreditGrant[];
  wallet_account_id?: string;
  topup_options?: {
    currency: string;
    minimum_cents: number;
    maximum_cents: number;
    quick_packs_cents: number[];
  };
}

export type ConsoleWalletScope = "shared" | "personal";
export interface ConsoleWalletScopeState {
  scope: ConsoleWalletScope;
  switching_moves_funds: boolean;
  existing_keys_keep_billing_owner?: boolean;
}

export async function fetchConsoleCredits(): Promise<ConsoleCredits> {
  if (CONSOLE_USE_MOCKS) {
    return {
      is_prepaid: false,
      balance_cents: 0,
      granted_cents: 0,
      consumed_cents: 0,
      grants: [],
      wallet_account_id: "org_demo",
      topup_options: {
        currency: "usd",
        minimum_cents: 500,
        maximum_cents: 100_000,
        quick_packs_cents: [500, 1000, 2000, 5000],
      },
    };
  }
  return consoleApi().get<ConsoleCredits>(`${BASE}/credits`);
}

export async function fetchConsoleWalletScope(): Promise<ConsoleWalletScopeState> {
  if (CONSOLE_USE_MOCKS) return { scope: "shared", switching_moves_funds: false };
  return consoleApi().get<ConsoleWalletScopeState>(`${BASE}/credits/scope`);
}

export async function updateConsoleWalletScope(
  scope: ConsoleWalletScope,
): Promise<ConsoleWalletScopeState> {
  if (CONSOLE_USE_MOCKS) {
    return { scope, switching_moves_funds: false, existing_keys_keep_billing_owner: true };
  }
  return consoleApi().put<ConsoleWalletScopeState>(`${BASE}/credits/scope`, { scope });
}

/** Start a self-serve credit top-up; returns the Stripe Checkout URL. */
export async function startConsoleCreditCheckout(
  amountUsd: number,
  returnUrl?: string,
  idempotencyKey?: string,
): Promise<string> {
  if (CONSOLE_USE_MOCKS) return "#mock-checkout";
  const res = await consoleApi().post<{ checkout_url: string }>(
    `${BASE}/credits/checkout`,
    { amount_cents: amountUsd * 100, return_url: returnUrl },
    { idempotencyKey },
  );
  return res.checkout_url;
}

/** Verify a Checkout order against Stripe; the return URL alone never proves payment. */
export async function reconcileConsoleCreditCheckout(
  orderId: string,
): Promise<{ status: "paid" | "pending" }> {
  return consoleApi().post<{ status: "paid" | "pending" }>(
    `${BASE}/credits/checkout/${encodeURIComponent(orderId)}/reconcile`,
    {},
  );
}

export interface ConsoleOnboardingState {
  id: string;
  organization_name: string;
  state: "org_provisioning" | "billing_pending" | "project_ready" | "active" | "failed";
  tenant_id: string | null;
  last_error: string | null;
}

export interface ConsoleOnboardingActivation extends ConsoleOnboardingState {
  project_created: boolean;
  next_step: string;
}

export interface ConsoleOnboardingOrganization {
  id: string;
  name: string;
  can_enable_engine: boolean;
}

export interface ConsoleBootstrapProject {
  created: boolean;
  key_minted: boolean;
  api_key: string | null;
  key_id: string | null;
  key_prefix: string | null;
  scopes: string[] | null;
}

/** Pre-entitlement-safe Engine onboarding endpoints. */
export async function fetchConsoleOnboarding(): Promise<ConsoleOnboardingState | null> {
  if (CONSOLE_USE_MOCKS) return null;
  return consoleApi().get<ConsoleOnboardingState | null>(`${BASE}/onboarding`);
}

export async function fetchConsoleOnboardingOrganization(): Promise<ConsoleOnboardingOrganization | null> {
  if (CONSOLE_USE_MOCKS) return null;
  return consoleApi().get<ConsoleOnboardingOrganization | null>(`${BASE}/onboarding/organization`);
}

export async function startConsoleOnboarding(
  organizationName: string,
  idempotencyKey: string,
): Promise<ConsoleOnboardingState> {
  if (CONSOLE_USE_MOCKS) {
    return {
      id: "mock-onboarding",
      organization_name: organizationName,
      state: "billing_pending",
      tenant_id: "mock-tenant",
      last_error: null,
    };
  }
  return consoleApi().post<ConsoleOnboardingState>(
    `${BASE}/onboarding`,
    { organization_name: organizationName },
    { idempotencyKey },
  );
}

export async function activateConsoleOnboarding(): Promise<ConsoleOnboardingActivation> {
  if (CONSOLE_USE_MOCKS) {
    return {
      id: "mock-onboarding",
      organization_name: "Mock organization",
      state: "active",
      tenant_id: "mock-tenant",
      last_error: null,
      project_created: true,
      next_step: "POST /platform/console/v1/projects with mint_key=true",
    };
  }
  return consoleApi().post<ConsoleOnboardingActivation>(`${BASE}/onboarding/activate`, {});
}

export async function createConsoleBootstrapProject(): Promise<ConsoleBootstrapProject> {
  if (CONSOLE_USE_MOCKS) {
    return {
      created: true,
      key_minted: true,
      api_key: "ando_sk_mock_once",
      key_id: "mock-key",
      key_prefix: "ando_sk_mock",
      scopes: ["query:read"],
    };
  }
  return consoleApi().post<ConsoleBootstrapProject>(`${BASE}/projects`, { mint_key: true });
}

// ── Billing (self-service portal + summary) ─────────────────────────────────

/** Prepaid vs postpaid — the tenant's collection mode. */
export type ConsoleBillingMode = "prepaid" | "postpaid";

/**
 * ``GET /billing/summary`` — one-call snapshot for the console header.
 * ``period_estimate_cents`` is the MARKED-UP month-to-date cost (the raw figure
 * and the markup % never cross the wire); ``last_invoice`` is the Stripe-sourced
 * most-recent invoice (null when none); ``next_invoice_date`` is null under the
 * default one-off invoice backend.
 */
export interface ConsoleBillingSummary {
  billing_mode: ConsoleBillingMode;
  currency: string;
  period_estimate_cents: number;
  next_invoice_date: string | null;
  has_payment_method: boolean;
  collection_method: "charge_automatically" | "send_invoice" | "license_contract" | null;
  stripe_customer_active: boolean;
  customer_created: number | null;
  customer_name: string | null;
  billing_email: string | null;
  billing_address: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
    country?: string | null;
  } | null;
  tax_exempt: string | null;
  account_balance_cents: number;
  delinquent: boolean;
  payment_method: {
    type: string | null;
    brand: string | null;
    last4: string | null;
    exp_month: number | null;
    exp_year: number | null;
    funding: string | null;
    country: string | null;
    is_default: boolean;
    email: string | null;
  } | null;
  last_invoice: ConsoleInvoice | null;
}

export async function fetchConsoleBillingSummary(): Promise<ConsoleBillingSummary> {
  if (CONSOLE_USE_MOCKS) {
    return {
      billing_mode: "postpaid",
      currency: "usd",
      period_estimate_cents: 0,
      next_invoice_date: null,
      has_payment_method: false,
      collection_method: "charge_automatically",
      stripe_customer_active: false,
      customer_created: null,
      customer_name: null,
      billing_email: null,
      billing_address: null,
      tax_exempt: null,
      account_balance_cents: 0,
      delinquent: false,
      payment_method: null,
      last_invoice: null,
    };
  }
  return consoleApi().get<ConsoleBillingSummary>(`${BASE}/billing/summary`);
}

/**
 * ``POST /billing/portal`` — mint a Stripe Customer Portal session URL (payment
 * methods, invoice history + PDF for both prepaid and postpaid). Returns the URL
 * to send the browser to; optional ``returnUrl`` is where the portal returns.
 */
export async function startConsoleBillingPortal(
  returnUrl?: string,
  flow: "manage" | "payment_method_update" = "manage",
): Promise<string> {
  if (CONSOLE_USE_MOCKS) return "#mock-portal";
  const res = await consoleApi().post<{ portal_url: string }>(`${BASE}/billing/portal`, {
    return_url: returnUrl,
    flow,
  });
  return res.portal_url;
}

/**
 * Direct-link builder for ``GET /invoices/{invoice_id}/pdf`` — the endpoint 302
 * redirects to the tenant's Stripe-hosted PDF, so the FE uses this as an
 * ``href`` (session cookies ride the navigation). Mirrors ``consoleApi()``'s
 * base-URL derivation: relative on web (Vite proxy / same-origin), absolute on
 * Tauri (strip the ``/api/v1`` suffix from ``apiUrl``).
 */
export function consoleInvoicePdfUrl(invoiceId: string): string {
  const { apiUrl, isTauri } = getEnv();
  const base = isTauri ? apiUrl.replace(/\/api\/v1\/?$/, "") : "";
  return `${base}${BASE}/invoices/${encodeURIComponent(invoiceId)}/pdf`;
}

// ── Members / team ──────────────────────────────────────────────────────────

/** ``GET /members`` — roster, pending invites, and seat usage. */
export async function fetchConsoleMembers(): Promise<ConsoleMembersResponse> {
  if (CONSOLE_USE_MOCKS) {
    return { members: [], invites: [], seats: { used: 0, total: null } };
  }
  return consoleApi().get<ConsoleMembersResponse>(`${BASE}/members`);
}

/** ``GET /members/roles`` — the assignable role catalog (static per build). */
export async function fetchConsoleMemberRoles(): Promise<ConsoleRolesResponse> {
  if (CONSOLE_USE_MOCKS) return { roles: [] };
  return consoleApi().get<ConsoleRolesResponse>(`${BASE}/members/roles`);
}

/**
 * ``POST /members/invites`` — invite a teammate by email. An existing user is
 * added straight away (``added`` / ``reactivated``), an unknown email is emailed
 * a tokenised invite (``invited``), and a user already in another org surfaces
 * as ``migration_required``. This Console endpoint grants Engine access while
 * preserving any existing Workspace access. Admin/owner only.
 */
export async function inviteConsoleMember(
  email: string,
  role: ConsoleMemberRole,
): Promise<ConsoleInviteResult> {
  if (CONSOLE_USE_MOCKS) {
    return { action: "invited", invite: null, member: null, current_org: null };
  }
  return consoleApi().post<ConsoleInviteResult>(`${BASE}/members/invites`, { email, role });
}

/** ``DELETE /members/invites/{invite_id}`` — revoke a pending invitation. */
export async function revokeConsoleInvite(inviteId: string): Promise<ConsoleInviteRevoked> {
  if (CONSOLE_USE_MOCKS) return { revoked: true, invite_id: inviteId };
  return consoleApi().delete<ConsoleInviteRevoked>(
    `${BASE}/members/invites/${encodeURIComponent(inviteId)}`,
  );
}

/** ``POST /members/invites/{invite_id}/resend`` — refresh the token + resend. */
export async function resendConsoleInvite(inviteId: string): Promise<ConsoleInviteResult> {
  if (CONSOLE_USE_MOCKS) {
    return { action: "invited", invite: null, member: null, current_org: null };
  }
  return consoleApi().post<ConsoleInviteResult>(
    `${BASE}/members/invites/${encodeURIComponent(inviteId)}/resend`,
  );
}

/** ``PATCH /members/{member_id}`` — change a teammate's role. Owner only. */
export async function updateConsoleMemberRole(
  memberId: string,
  role: ConsoleMemberRole,
): Promise<ConsoleMember> {
  if (CONSOLE_USE_MOCKS) {
    return {
      member_id: memberId,
      email: null,
      display_name: null,
      role,
      status: "active",
      last_active_at: null,
      joined_at: null,
    };
  }
  return consoleApi().patch<ConsoleMember>(`${BASE}/members/${encodeURIComponent(memberId)}`, {
    role,
  });
}

/** ``DELETE /members/{member_id}`` — remove a teammate. Admin/owner only. */
export async function removeConsoleMember(memberId: string): Promise<ConsoleMemberRemoved> {
  if (CONSOLE_USE_MOCKS) return { removed: true, member_id: memberId };
  return consoleApi().delete<ConsoleMemberRemoved>(
    `${BASE}/members/${encodeURIComponent(memberId)}`,
  );
}

// ── Keys ──────────────────────────────────────────────────────────────────

export async function fetchConsoleKeys(): Promise<ConsoleKeysResponse> {
  if (CONSOLE_USE_MOCKS) return mockListKeys();
  return consoleApi().get<ConsoleKeysResponse>(`${BASE}/keys`);
}

/**
 * Storage key for the last-known-good ``/keys/scopes`` response. Versioned
 * like the other persisted console state (``ando.console-sidebar.v1``).
 */
export const CONSOLE_KEY_SCOPES_CACHE_KEY = "ando.console-key-scopes.v1";

function isConsoleScopeInfo(data: unknown): data is ConsoleScopeInfo {
  if (typeof data !== "object" || data === null) return false;
  const { id, label, description } = data as Record<string, unknown>;
  return (
    typeof id === "string" &&
    id.length > 0 &&
    typeof label === "string" &&
    typeof description === "string"
  );
}

/** Shape guard shared by the wire response and the persisted cache copy. */
export function isConsoleScopesResponse(data: unknown): data is ConsoleScopesResponse {
  if (typeof data !== "object" || data === null) return false;
  const { scopes } = data as { scopes?: unknown };
  return Array.isArray(scopes) && scopes.length > 0 && scopes.every(isConsoleScopeInfo);
}

/** Last-known-good catalog persisted by ``fetchConsoleKeyScopes`` — or null. */
export async function readCachedConsoleKeyScopes(): Promise<ConsoleScopesResponse | null> {
  const cached = await getStorage().get<unknown>(CONSOLE_KEY_SCOPES_CACHE_KEY);
  return isConsoleScopesResponse(cached) ? cached : null;
}

export async function writeCachedConsoleKeyScopes(res: ConsoleScopesResponse): Promise<void> {
  await getStorage().set(CONSOLE_KEY_SCOPES_CACHE_KEY, res);
}

/**
 * ``GET /keys/scopes`` — the authoritative scope catalog the wizard renders
 * and submits. The endpoint is the SINGLE seam for the scope vocabulary:
 * there is no hardcoded FE copy. Every valid response is persisted as the
 * last-known-good catalog; when the endpoint errors (or ships a malformed
 * body) that cached copy is served, so the wizard survives transient blips
 * without ever inventing ids the API could 400 on. With no cache either,
 * the error surfaces and the wizard shows its retry state.
 */
export async function fetchConsoleKeyScopes(): Promise<ConsoleScopesResponse> {
  if (CONSOLE_USE_MOCKS) return mockListKeyScopes();
  let res: unknown;
  try {
    res = await consoleApi().get<unknown>(`${BASE}/keys/scopes`);
  } catch (err) {
    const cached = await readCachedConsoleKeyScopes();
    if (cached) return cached;
    throw err;
  }
  if (isConsoleScopesResponse(res)) {
    await writeCachedConsoleKeyScopes(res);
    return res;
  }
  const cached = await readCachedConsoleKeyScopes();
  if (cached) return cached;
  throw new Error("Catalogo degli scope non disponibile. Riprova.");
}

export async function createConsoleKey(
  input: ConsoleCreateKeyRequest,
): Promise<ConsoleCreateKeyResponse> {
  if (CONSOLE_USE_MOCKS) return mockCreateKey(input);
  return consoleApi().post<ConsoleCreateKeyResponse>(`${BASE}/keys`, input);
}

export async function revokeConsoleKey(keyId: string): Promise<void> {
  if (CONSOLE_USE_MOCKS) return mockRevokeKey(keyId);
  await consoleApi().post<unknown>(`${BASE}/keys/${encodeURIComponent(keyId)}/revoke`);
}

export async function fetchConsoleKeyAudit(keyId: string): Promise<ConsoleKeyAuditResponse> {
  if (CONSOLE_USE_MOCKS) return mockKeyAudit(keyId);
  return consoleApi().get<ConsoleKeyAuditResponse>(
    `${BASE}/keys/${encodeURIComponent(keyId)}/audit`,
  );
}

/**
 * ``POST /keys/{key_id}/roll`` — rotate a key: mint a successor inheriting the
 * old key's name/scopes/policy/tier and open a ``graceHours`` window on the old
 * key (its ``expires_at`` becomes ``now + graceHours``) so the secret swaps with
 * zero downtime. The response carries the new full secret EXACTLY ONCE (same
 * shape as create). ``graceHours = 0`` revokes the old key immediately
 * (``old_key_revoked = true``, ``old_key_expires_at = null``).
 */
export async function rollConsoleKey(keyId: string, graceHours: number): Promise<ConsoleKeyRolled> {
  if (CONSOLE_USE_MOCKS) {
    return {
      api_key: "ando_sk_live_mockrotatedsecret",
      key_id: "mock-rotated",
      key_prefix: "ando_sk_live_moc",
      scopes: [],
      resource_policy: null,
      old_key_id: keyId,
      old_key_expires_at: null,
      old_key_revoked: graceHours === 0,
    };
  }
  return consoleApi().post<ConsoleKeyRolled>(`${BASE}/keys/${encodeURIComponent(keyId)}/roll`, {
    grace_hours: graceHours,
  });
}

/**
 * ``PATCH /keys/{key_id}`` — edit a key in place (its ``scopes`` and/or
 * ``resourcePolicy``, spend cap included) without minting a new secret. Omitted
 * fields are left untouched (true PATCH semantics); provide at least one. A
 * revoked key cannot be edited (409). Returns the updated key summary.
 */
export async function updateConsoleKey(
  keyId: string,
  patch: { scopes?: string[]; resourcePolicy?: ConsoleResourcePolicy | null },
): Promise<ConsoleApiKey> {
  if (CONSOLE_USE_MOCKS) {
    return {
      key_id: keyId,
      name: "",
      key_prefix: "",
      scopes: patch.scopes ?? [],
      resource_policy: patch.resourcePolicy ?? null,
      created_at: new Date().toISOString(),
      expires_at: null,
      last_used_at: null,
      revoked_at: null,
    };
  }
  // Only forward provided fields — an omitted key is untouched server-side.
  const body: ConsoleKeyUpdateRequest = {};
  if (patch.scopes !== undefined) body.scopes = patch.scopes;
  if (patch.resourcePolicy !== undefined) body.resource_policy = patch.resourcePolicy;
  return consoleApi().patch<ConsoleApiKey>(`${BASE}/keys/${encodeURIComponent(keyId)}`, body);
}

// ── Quotas (rate-limit + spend-cap visibility) ─────────────────────────────

/**
 * ``GET /quotas`` — every AppKey of the project with its rate-limit tier limit,
 * live in-window usage + headroom, and monthly spend-cap posture. Read-only.
 * ``current_usage`` / ``remaining`` are ``null`` when the limiter's Redis is
 * unavailable (fail-soft).
 */
export async function fetchConsoleQuotas(): Promise<ConsoleQuotasResponse> {
  if (CONSOLE_USE_MOCKS) return { keys: [], count: 0 };
  return consoleApi().get<ConsoleQuotasResponse>(`${BASE}/quotas`);
}

// ── Connectors ────────────────────────────────────────────────────────────

export async function fetchConsoleConnectors(): Promise<ConsoleConnectorsResponse> {
  if (CONSOLE_USE_MOCKS) return mockListConnectors();
  return consoleApi().get<ConsoleConnectorsResponse>(`${BASE}/connectors`);
}

export async function fetchConsoleConnectorDetail(
  connectionId: string,
): Promise<ConsoleConnectorDetail> {
  if (CONSOLE_USE_MOCKS) return mockConnectorDetail(connectionId);
  return consoleApi().get<ConsoleConnectorDetail>(
    `${BASE}/connectors/${encodeURIComponent(connectionId)}`,
  );
}

/**
 * Start (or re-start, for reconnect) the EXISTING product connect flow for a
 * capability. Returns the product's own entrypoint decision
 * (``redirect_url`` + ``flow`` + resolved ``vendor``); the caller sends the
 * browser to ``redirect_url`` for an OAuth vendor. ``vendor`` is optional —
 * the BE defaults to the capability's primary vendor. The MOCK completes the
 * connection inline and returns an empty ``redirect_url`` — callers must
 * treat an empty string as "refetch, no redirect".
 */
export async function connectConsoleConnector(
  type: string,
  vendor?: string | null,
): Promise<ConsoleConnectResponse> {
  if (CONSOLE_USE_MOCKS) return mockConnectConnector(type, vendor ?? undefined);
  return consoleApi().post<ConsoleConnectResponse>(
    `${BASE}/connectors/${encodeURIComponent(type)}/connect`,
    vendor ? { vendor } : undefined,
  );
}

export async function syncConsoleConnector(connectionId: string): Promise<void> {
  if (CONSOLE_USE_MOCKS) return mockSyncConnector(connectionId);
  await consoleApi().post<unknown>(`${BASE}/connectors/${encodeURIComponent(connectionId)}/sync`);
}

export async function disconnectConsoleConnector(connectionId: string): Promise<void> {
  if (CONSOLE_USE_MOCKS) return mockDisconnectConnector(connectionId);
  await consoleApi().delete<unknown>(`${BASE}/connectors/${encodeURIComponent(connectionId)}`);
}

// ── Custom connectors (developer Push-API) ────────────────────────────────

/** ``GET /custom-connectors`` — the project's own Push-API connectors (newest first). */
export async function listCustomConnectors(): Promise<ConsoleCustomConnectorsResponse> {
  if (CONSOLE_USE_MOCKS) return mockListCustomConnectors();
  return consoleApi().get<ConsoleCustomConnectorsResponse>(`${BASE}/custom-connectors`);
}

/** ``GET /custom-connectors/{slug}`` — one connector, or a ``not_found`` envelope. */
export async function getCustomConnector(slug: string): Promise<ConsoleCustomConnector> {
  if (CONSOLE_USE_MOCKS) return mockGetCustomConnector(slug);
  return consoleApi().get<ConsoleCustomConnector>(
    `${BASE}/custom-connectors/${encodeURIComponent(slug)}`,
  );
}

/**
 * ``POST /custom-connectors`` — register a Push-API connector. ``slug`` must
 * be unique in the project and match the slug pattern (see ``lib/connectorSlug``);
 * a bad or duplicate slug comes back as a 400 ``invalid_request`` envelope.
 */
export async function createCustomConnector(
  input: ConsoleCustomConnectorCreate,
): Promise<ConsoleCustomConnector> {
  if (CONSOLE_USE_MOCKS) return mockCreateCustomConnector(input);
  return consoleApi().post<ConsoleCustomConnector>(`${BASE}/custom-connectors`, input);
}

/**
 * ``PATCH /custom-connectors/{slug}`` — rename, re-enable/disable, or replace
 * the config. For manifest connectors this is also the SECRET-ROTATION path:
 * a fresh ``manifest.auth.secret`` in ``config`` rotates the sealed
 * credential, omitting it keeps the current one; the config is replaced whole
 * and the cursor state resets (next run is a clean full sync).
 */
export async function rebindManifestPrincipal(
  slug: string,
  keyId: string,
): Promise<ConsoleCustomConnector> {
  if (CONSOLE_USE_MOCKS) return mockRebindManifestPrincipal(slug, keyId);
  return consoleApi().post<ConsoleCustomConnector>(
    `${BASE}/custom-connectors/${encodeURIComponent(slug)}/principal/rebind`,
    { key_id: keyId },
  );
}

export async function updateCustomConnector(
  slug: string,
  patch: ConsoleCustomConnectorUpdate,
): Promise<ConsoleCustomConnector> {
  if (CONSOLE_USE_MOCKS) return mockUpdateCustomConnector(slug, patch);
  return consoleApi().patch<ConsoleCustomConnector>(
    `${BASE}/custom-connectors/${encodeURIComponent(slug)}`,
    patch,
  );
}

/**
 * ``DELETE /custom-connectors/{slug}`` — DISABLE, not destroy: new syncs stop
 * but everything already indexed stays queryable via ``/query`` and ``/agentic``.
 */
export async function disableCustomConnector(
  slug: string,
): Promise<ConsoleCustomConnectorDisableResponse> {
  if (CONSOLE_USE_MOCKS) return mockDisableCustomConnector(slug);
  return consoleApi().delete<ConsoleCustomConnectorDisableResponse>(
    `${BASE}/custom-connectors/${encodeURIComponent(slug)}`,
  );
}

/**
 * ``GET /custom-connectors/kinds`` — the connector kinds + the Record contract
 * (fields, required flags, batch/size limits). STATIC and project-independent;
 * the session projection of the runtime ``GET /connectors/kinds``.
 */
export async function fetchConsoleConnectorKinds(): Promise<ConsoleConnectorKindsResponse> {
  if (CONSOLE_USE_MOCKS) return mockConnectorKinds();
  return consoleApi().get<ConsoleConnectorKindsResponse>(`${BASE}/custom-connectors/kinds`);
}

/**
 * ``POST /custom-connectors/{slug}/validate`` — DRY-RUN a batch of records
 * against the Record contract. Nothing is written; each record comes back
 * either accepted (with a preview of what it would index) or rejected with the
 * offending field + reason.
 */
export async function validateConsoleConnectorRecords(
  slug: string,
  records: Array<Record<string, unknown>>,
): Promise<ConsoleRecordValidateResponse> {
  if (CONSOLE_USE_MOCKS) return mockValidateConnectorRecords(slug, records);
  return consoleApi().post<ConsoleRecordValidateResponse>(
    `${BASE}/custom-connectors/${encodeURIComponent(slug)}/validate`,
    { records },
  );
}

/**
 * ``GET /custom-connectors/{slug}/syncs`` — the connector's sync-run history
 * (per run: status + indexed/deleted/rejected counts + duration), newest first.
 */
export async function fetchConsoleConnectorSyncs(
  slug: string,
  limit = 20,
): Promise<ConsoleSyncListResponse> {
  if (CONSOLE_USE_MOCKS) return mockConnectorSyncs(slug, limit);
  return consoleApi().get<ConsoleSyncListResponse>(
    `${BASE}/custom-connectors/${encodeURIComponent(slug)}/syncs?limit=${encodeURIComponent(limit)}`,
  );
}

// ── Custom-connector actions (the write-loop) ──────────────────────────────

/**
 * ``GET /custom-connectors/{slug}/actions`` — the actions a connector declares
 * it can perform. Each carries its params JSON-Schema + risk level.
 */
export async function listConnectorActions(
  slug: string,
): Promise<ConsoleActionDefinitionsResponse> {
  if (CONSOLE_USE_MOCKS) return mockListConnectorActions(slug);
  return consoleApi().get<ConsoleActionDefinitionsResponse>(
    `${BASE}/custom-connectors/${encodeURIComponent(slug)}/actions`,
  );
}

/**
 * ``POST /custom-connectors/{slug}/actions`` — declare an action. ``name`` must
 * be unique per connector and match the action-name grammar; a bad or duplicate
 * name comes back as a 400 ``invalid_request`` envelope.
 */
export async function declareConnectorAction(
  slug: string,
  input: ConsoleActionDefinitionCreate,
): Promise<ConsoleActionDefinition> {
  if (CONSOLE_USE_MOCKS) return mockDeclareConnectorAction(slug, input);
  return consoleApi().post<ConsoleActionDefinition>(
    `${BASE}/custom-connectors/${encodeURIComponent(slug)}/actions`,
    input,
  );
}

/** ``DELETE /custom-connectors/{slug}/actions/{name}`` — remove a declared action. */
export async function deleteConnectorAction(
  slug: string,
  name: string,
): Promise<ConsoleActionDefinitionDeleteResponse> {
  if (CONSOLE_USE_MOCKS) return mockDeleteConnectorAction(slug, name);
  return consoleApi().delete<ConsoleActionDefinitionDeleteResponse>(
    `${BASE}/custom-connectors/${encodeURIComponent(slug)}/actions/${encodeURIComponent(name)}`,
  );
}

/**
 * ``POST /custom-connectors/{slug}/actions/{name}/invoke`` — run an HTTP-bound
 * capability SYNCHRONOUSLY: params are validated against the declared schema,
 * high/critical risk requires ``confirm_risk:true`` (else a 400 — never a
 * silent execute), then the capability executor calls the connector's bound
 * endpoint and relays ``{ok, status_code, response}``. Only meaningful for
 * actions declared WITH a ``binding`` — webhook-only actions go through the
 * propose→execute loop instead.
 */
export async function invokeConnectorAction(
  slug: string,
  name: string,
  body?: ConsoleActionInvokeRequest,
): Promise<ConsoleActionInvokeResponse> {
  if (CONSOLE_USE_MOCKS) return mockInvokeConnectorAction(slug, name, body);
  return consoleApi().post<ConsoleActionInvokeResponse>(
    `${BASE}/custom-connectors/${encodeURIComponent(slug)}/actions/${encodeURIComponent(name)}/invoke`,
    body ?? {},
  );
}

/**
 * ``POST /actions`` — propose an action request. The response is "proposed" and
 * carries a ``preview`` of what will run; nothing is dispatched until
 * ``executeAction``. Params that fail the action's schema come back as a 400.
 */
export async function proposeAction(
  input: ConsoleActionRequestCreate,
): Promise<ConsoleActionRequest> {
  if (CONSOLE_USE_MOCKS) return mockProposeAction(input);
  return consoleApi().post<ConsoleActionRequest>(`${BASE}/actions`, input);
}

/**
 * ``POST /actions/{id}/execute`` — dispatch a proposed request to the
 * developer's app. ``confirm_risk:true`` is REQUIRED for high/critical actions
 * (else 400). When no webhook is subscribed to ``action.requested`` the request
 * fails and the API returns a 400 telling the developer to register one.
 */
export async function executeAction(
  id: string,
  body?: ConsoleActionExecuteRequest,
): Promise<ConsoleActionRequest> {
  if (CONSOLE_USE_MOCKS) return mockExecuteAction(id, body);
  return consoleApi().post<ConsoleActionRequest>(
    `${BASE}/actions/${encodeURIComponent(id)}/execute`,
    body ?? {},
  );
}

/** ``GET /actions/{id}`` — one action request, or a ``not_found`` envelope. */
export async function getActionRequest(id: string): Promise<ConsoleActionRequest> {
  if (CONSOLE_USE_MOCKS) return mockGetActionRequest(id);
  return consoleApi().get<ConsoleActionRequest>(`${BASE}/actions/${encodeURIComponent(id)}`);
}

/** ``GET /actions?connector={slug}&limit={n}`` — recent requests for a connector. */
export async function listActionRequests(
  slug: string,
  limit = 20,
): Promise<ConsoleActionRequestsResponse> {
  if (CONSOLE_USE_MOCKS) return mockListActionRequests(slug, limit);
  return consoleApi().get<ConsoleActionRequestsResponse>(
    `${BASE}/actions?connector=${encodeURIComponent(slug)}&limit=${encodeURIComponent(limit)}`,
  );
}

// ── Webhooks (console session projection) ──────────────────────────────────

/**
 * ``POST /webhooks`` — register an HTTPS endpoint for signed event
 * deliveries (Standard Webhooks). The response ``secret`` (``whsec_``)
 * appears EXACTLY ONCE — it is stored encrypted server-side and never
 * re-shown. A non-HTTPS URL or an unknown event is a 400 ``invalid_request``.
 */
export async function createConsoleWebhook(
  input: ConsoleWebhookCreate,
): Promise<ConsoleWebhookCreated> {
  if (CONSOLE_USE_MOCKS) return mockCreateWebhook(input);
  return consoleApi().post<ConsoleWebhookCreated>(`${BASE}/webhooks`, input);
}

/** ``GET /webhooks`` — the project's endpoints (display-safe, never a secret). */
export async function fetchConsoleWebhooks(): Promise<ConsoleWebhooksResponse> {
  if (CONSOLE_USE_MOCKS) return mockListWebhooks();
  return consoleApi().get<ConsoleWebhooksResponse>(`${BASE}/webhooks`);
}

/** ``DELETE /webhooks/{id}`` — remove an endpoint (deliveries stop). */
export async function deleteConsoleWebhook(webhookId: string): Promise<ConsoleWebhookDeleted> {
  if (CONSOLE_USE_MOCKS) return mockDeleteWebhook(webhookId);
  return consoleApi().delete<ConsoleWebhookDeleted>(
    `${BASE}/webhooks/${encodeURIComponent(webhookId)}`,
  );
}

/**
 * ``POST /webhooks/{id}/test`` — synchronous ``test.ping`` delivery to verify
 * the receiver's signature handling. A failing receiver comes back in the 200
 * body (``delivered:false`` + ``error``), not as an API error.
 */
export async function testConsoleWebhook(webhookId: string): Promise<ConsoleWebhookTestResult> {
  if (CONSOLE_USE_MOCKS) return mockTestWebhook(webhookId);
  return consoleApi().post<ConsoleWebhookTestResult>(
    `${BASE}/webhooks/${encodeURIComponent(webhookId)}/test`,
  );
}

/**
 * ``GET /webhooks/{id}/deliveries`` — the endpoint's delivery history,
 * newest first (pending / delivered / failed + the signed payload).
 */
export async function getConsoleWebhookDeliveries(
  webhookId: string,
): Promise<ConsoleWebhookDeliveriesResponse> {
  if (CONSOLE_USE_MOCKS) return mockGetWebhookDeliveries(webhookId);
  return consoleApi().get<ConsoleWebhookDeliveriesResponse>(
    `${BASE}/webhooks/${encodeURIComponent(webhookId)}/deliveries`,
  );
}

/**
 * ``POST /webhooks/deliveries/{id}/replay`` — re-queue one delivery with an
 * immediate retry and a FRESH signature (works on delivered ones too).
 */
export async function replayConsoleWebhookDelivery(
  deliveryId: string,
): Promise<ConsoleWebhookDeliveryReplayed> {
  if (CONSOLE_USE_MOCKS) return mockReplayWebhookDelivery(deliveryId);
  return consoleApi().post<ConsoleWebhookDeliveryReplayed>(
    `${BASE}/webhooks/deliveries/${encodeURIComponent(deliveryId)}/replay`,
  );
}

/**
 * ``PATCH /webhooks/{webhook_id}`` — enable/disable event deliveries for one
 * endpoint (the only mutable field). Disabling stops both ends of the pipe
 * (reversible); re-enabling resumes deliveries, including rows queued earlier.
 */
export async function setConsoleWebhookActive(
  webhookId: string,
  active: boolean,
): Promise<ConsoleWebhookSummary> {
  if (CONSOLE_USE_MOCKS) {
    return { webhook_id: webhookId, url: "", events: [], active, created_at: null };
  }
  return consoleApi().patch<ConsoleWebhookSummary>(
    `${BASE}/webhooks/${encodeURIComponent(webhookId)}`,
    { active },
  );
}

/**
 * ``POST /webhooks/{webhook_id}/rotate-secret`` — mint a NEW ``whsec_`` signing
 * secret and return it EXACTLY ONCE (same one-time-reveal contract as create).
 * IMMEDIATE REPLACE: the new secret takes effect for the next delivery and the
 * old one stops verifying at once — update the receiver BEFORE its next event.
 */
export async function rotateConsoleWebhookSecret(
  webhookId: string,
): Promise<ConsoleWebhookSecretRotated> {
  if (CONSOLE_USE_MOCKS) {
    return {
      webhook_id: webhookId,
      url: "",
      events: [],
      secret: "whsec_mockrotatedsecret",
      active: true,
      created_at: null,
    };
  }
  return consoleApi().post<ConsoleWebhookSecretRotated>(
    `${BASE}/webhooks/${encodeURIComponent(webhookId)}/rotate-secret`,
  );
}

// ── Documents / jobs ──────────────────────────────────────────────────────

export interface ConsoleDocumentFilters {
  search?: string;
  status?: ConsoleJobStatus;
  sort_by?: "name" | "size" | "created_at";
  sort_order?: "asc" | "desc";
}

export async function fetchConsoleDocuments(
  page: number,
  filters: ConsoleDocumentFilters = {},
): Promise<ConsoleDocumentsPage> {
  if (CONSOLE_USE_MOCKS) return normalizeConsoleDocumentsPage(await mockListDocuments(page));
  const params = new URLSearchParams({ limit: "20", offset: String((page - 1) * 20) });
  if (filters.search) params.set("search", filters.search);
  if (filters.status)
    params.set("status", filters.status === "running" ? "in_progress" : filters.status);
  if (filters.sort_by)
    params.set(
      "sort_by",
      filters.sort_by === "name"
        ? "filename"
        : filters.sort_by === "size"
          ? "size_bytes"
          : filters.sort_by,
    );
  if (filters.sort_order) params.set("sort_order", filters.sort_order);
  return normalizeConsoleDocumentsPage(
    await consoleApi().get<unknown>(`${BASE}/documents?${params.toString()}`),
  );
}

export async function fetchConsoleJobs(status?: ConsoleJobStatus): Promise<ConsoleJobsResponse> {
  if (CONSOLE_USE_MOCKS) return mockListJobs(status);
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return consoleApi().get<ConsoleJobsResponse>(`${BASE}/jobs${qs}`);
}

export async function uploadConsoleDocument(file: File): Promise<ConsoleDocument> {
  if (CONSOLE_USE_MOCKS) return mockUploadDocument(file.name, file.size);
  const form = new FormData();
  form.append("file", file);
  return consoleApi().postMultipart<ConsoleDocument>(`${BASE}/documents`, form);
}

export async function deleteConsoleDocument(documentId: string): Promise<void> {
  if (CONSOLE_USE_MOCKS) return mockDeleteDocument(documentId);
  await consoleApi().delete<unknown>(`${BASE}/documents/${encodeURIComponent(documentId)}`);
}

/** Read and replace the ACL of a Console source. The server validates owner and principals. */
export async function fetchConsoleAccessOverview(offset = 0, limit = 50, resourceKind?: "document" | "connection"): Promise<ConsoleAccessOverviewPage> {
  return consoleApi().get<ConsoleAccessOverviewPage>(
    `${BASE}/resources/access-overview?limit=${limit}&offset=${offset}${resourceKind ? `&resource_kind=${resourceKind}` : ""}`,
  );
}

export async function fetchConsoleResourceAccess(
  kind: "document" | "connection",
  id: string,
): Promise<ConsoleResourceAccess> {
  return consoleApi().get<ConsoleResourceAccess>(
    `${BASE}/resources/${kind}/${encodeURIComponent(id)}/access`,
  );
}

export async function updateConsoleResourceAccess(
  kind: "document" | "connection",
  id: string,
  access: Pick<ConsoleResourceAccess, "visibility" | "grants">,
): Promise<ConsoleResourceAccess> {
  return consoleApi().put<ConsoleResourceAccess>(
    `${BASE}/resources/${kind}/${encodeURIComponent(id)}/access`,
    access,
  );
}

/** Browse only content already indexed in Ando, never the provider account. */
export async function fetchConsoleIndexedKnowledgeItems(
  connectionId: string, search = "", offset = 0, limit = 50,
): Promise<ConsoleIndexedKnowledgeItemsPage> {
  const params = new URLSearchParams({ search, offset: String(offset), limit: String(limit) });
  return consoleApi().get<ConsoleIndexedKnowledgeItemsPage>(
    `${BASE}/resources/connection/${encodeURIComponent(connectionId)}/indexed-items?${params}`,
  );
}

export async function saveConsoleIndexedKnowledgeSelection(
  connectionId: string, itemIds: string[],
): Promise<{ provider: string; item_ids: string[] }> {
  return consoleApi().put<{ provider: string; item_ids: string[] }>(
    `${BASE}/resources/connection/${encodeURIComponent(connectionId)}/indexed-share-selection`,
    { item_ids: itemIds },
  );
}

/** Page through the saved Ando item selection without returning a large policy payload. */
export async function fetchConsoleSelectedKnowledgeItems(
  connectionId: string, offset = 0, limit = 50,
): Promise<ConsoleSelectedKnowledgePage> {
  const params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
  return consoleApi().get<ConsoleSelectedKnowledgePage>(
    `${BASE}/resources/connection/${encodeURIComponent(connectionId)}/indexed-share-items?${params}`,
  );
}

/** Apply additions and removals together; any change revokes existing recipients. */
export async function patchConsoleSelectedKnowledgeItems(
  connectionId: string, addItemIds: string[], removeItemIds: string[],
): Promise<{ provider: string; added_count: number; removed_count: number; selected_count: number }> {
  return consoleApi().patch(
    `${BASE}/resources/connection/${encodeURIComponent(connectionId)}/indexed-share-items`,
    { add_item_ids: addItemIds, remove_item_ids: removeItemIds },
  );
}

export async function fetchConsoleDocumentJobs(documentId: string): Promise<ConsoleJobsResponse> {
  if (CONSOLE_USE_MOCKS) return mockDocumentJobs(documentId);
  return consoleApi().get<ConsoleJobsResponse>(
    `${BASE}/documents/${encodeURIComponent(documentId)}/jobs`,
  );
}

export async function fetchConsoleDocumentContent(
  documentId: string,
): Promise<ConsoleDocumentContent> {
  return consoleApi().get<ConsoleDocumentContent>(
    `${BASE}/documents/${encodeURIComponent(documentId)}/content`,
  );
}

// ── Usage ─────────────────────────────────────────────────────────────────

export async function fetchConsoleUsage(window: ConsoleUsageWindow = "30d"): Promise<ConsoleUsage> {
  if (CONSOLE_USE_MOCKS) return mockUsage(window);
  return consoleApi().get<ConsoleUsage>(`${BASE}/usage?window=${encodeURIComponent(window)}`);
}

/**
 * ``GET /usage/breakdown`` — the tenant's OWN LLM consumption: requests +
 * tokens over time, by model (top consumers) and by purpose, with the window
 * totals and the previous-period baseline. COUNTS + relative % only, never a
 * currency figure.
 */
export async function fetchConsoleUsageBreakdown(
  window: ConsoleUsageWindow = "30d",
): Promise<ConsoleUsageBreakdown> {
  if (CONSOLE_USE_MOCKS) return mockUsageBreakdown(window);
  return consoleApi().get<ConsoleUsageBreakdown>(
    `${BASE}/usage/breakdown?window=${encodeURIComponent(window)}`,
  );
}

/**
 * ``GET /keys/{key_id}/usage?window=`` — one key's LLM consumption: the
 * zero-filled per-day trend, top consuming models, every call purpose and the
 * window totals — each carrying the versioned, marked-up ``cost_usd`` (this is
 * a per-key billing view, so cost IS surfaced here). An unknown / another
 * project's key yields an all-zero breakdown.
 */
export async function fetchConsoleKeyUsage(
  keyId: string,
  window: ConsoleUsageWindow = "30d",
): Promise<ConsoleKeyUsage> {
  if (CONSOLE_USE_MOCKS) {
    return {
      window,
      key_id: keyId,
      days: [],
      totals: { requests: 0, tokens: 0, cost_usd: 0 },
      by_model: [],
      by_source: [],
    };
  }
  return consoleApi().get<ConsoleKeyUsage>(
    `${BASE}/keys/${encodeURIComponent(keyId)}/usage?window=${encodeURIComponent(window)}`,
  );
}

/**
 * ``GET /usage/by-key?window=`` — the project's AppKeys RANKED by LLM
 * consumption over the window (highest-token first), each row carrying the key's
 * display ``name`` (``null`` for a since-deleted key) and marked-up ``cost_usd``,
 * plus the window totals across every key.
 */
export async function fetchConsoleUsageByKey(
  window: ConsoleUsageWindow = "30d",
): Promise<ConsoleUsageByKey> {
  if (CONSOLE_USE_MOCKS) {
    return { window, by_key: [], totals: { requests: 0, tokens: 0, cost_usd: 0 } };
  }
  return consoleApi().get<ConsoleUsageByKey>(
    `${BASE}/usage/by-key?window=${encodeURIComponent(window)}`,
  );
}

// ── Models + BYOK ─────────────────────────────────────────────────────────

/**
 * ``GET /models`` — every purpose's effective model (chosen vs inherited)
 * plus the selectable catalog per purpose. The catalog carries the LATEST
 * real models (Gemini platform defaults + the current Claude tiers reachable
 * through OpenRouter); it never invents a model the deployment cannot serve.
 */
export async function fetchConsoleModels(): Promise<ConsoleModelsResponse> {
  if (CONSOLE_USE_MOCKS) return mockListModels();
  return consoleApi().get<ConsoleModelsResponse>(`${BASE}/models`);
}

/** ``PUT /models/{purpose}`` — choose the provider + model of one purpose. */
export async function setConsoleModel(
  purpose: ConsoleModelPurpose,
  body: ConsoleSetModelRequest,
): Promise<ConsoleModelConfigResponse> {
  if (CONSOLE_USE_MOCKS) return mockSetModel(purpose, body);
  return consoleApi().put<ConsoleModelConfigResponse>(
    `${BASE}/models/${encodeURIComponent(purpose)}`,
    body,
  );
}

/** ``DELETE /models/{purpose}`` — revert a purpose to the platform default. */
export async function revertConsoleModel(
  purpose: ConsoleModelPurpose,
): Promise<ConsoleModelRevertResponse> {
  if (CONSOLE_USE_MOCKS) return mockRevertModel(purpose);
  return consoleApi().delete<ConsoleModelRevertResponse>(
    `${BASE}/models/${encodeURIComponent(purpose)}`,
  );
}

/**
 * ``POST /models/{purpose}/byok`` — store or ROTATE the org's own provider
 * key. Write-only: the response carries a mask + last 4, never the plaintext.
 */
export async function setConsoleByok(
  purpose: ConsoleModelPurpose,
  apiKey: string,
): Promise<ConsoleModelConfigResponse> {
  if (CONSOLE_USE_MOCKS) return mockSetByok(purpose, apiKey);
  return consoleApi().post<ConsoleModelConfigResponse>(
    `${BASE}/models/${encodeURIComponent(purpose)}/byok`,
    { api_key: apiKey },
  );
}

/** ``DELETE /models/{purpose}/byok`` — drop the org's own provider key. */
export async function deleteConsoleByok(
  purpose: ConsoleModelPurpose,
): Promise<ConsoleByokRemovedResponse> {
  if (CONSOLE_USE_MOCKS) return mockDeleteByok(purpose);
  return consoleApi().delete<ConsoleByokRemovedResponse>(
    `${BASE}/models/${encodeURIComponent(purpose)}/byok`,
  );
}

// ── Schema curation (K-Schema publish gate) ────────────────────────────────

/**
 * ``GET /sources/{id}/schema`` — the profiled K-Schema of one structured
 * source, with the publish gate reflected in ``queryable``/``reason`` and
 * every object's AI-proposed + human-curated descriptions. A ``not_found``
 * envelope when the id is not a structured source of the project.
 */
export async function getConsoleSourceSchema(sourceId: string): Promise<ConsoleSchemaResponse> {
  if (CONSOLE_USE_MOCKS) return mockGetSourceSchema(sourceId);
  return consoleApi().get<ConsoleSchemaResponse>(
    `${BASE}/sources/${encodeURIComponent(sourceId)}/schema`,
  );
}

/**
 * ``PATCH /sources/{id}/schema/{object}`` — override the AI-proposed
 * description(s) of one object. The human edit wins and is protected from
 * re-profiling (``user_edited=true``). An empty patch comes back as a 400.
 */
export async function curateConsoleSchemaObject(
  sourceId: string,
  objectName: string,
  body: ConsoleSchemaObjectCuration,
): Promise<ConsoleSchemaObjectView> {
  if (CONSOLE_USE_MOCKS) return mockCurateSchemaObject(sourceId, objectName, body);
  return consoleApi().patch<ConsoleSchemaObjectView>(
    `${BASE}/sources/${encodeURIComponent(sourceId)}/schema/${encodeURIComponent(objectName)}`,
    body,
  );
}

/**
 * ``PATCH /sources/{id}/name`` — set the console display name (alias) of a
 * structured source, so otherwise-identical capability entries read as
 * distinct in the Schema map. Blank clears the alias (reverts to the derived
 * default). Console-only: the public ``/v1/sources`` never exposes this name.
 */
export async function renameConsoleSource(
  sourceId: string,
  name: string,
): Promise<ConsoleRenamedSource> {
  if (CONSOLE_USE_MOCKS) return mockRenameSource(sourceId, name);
  return consoleApi().patch<ConsoleRenamedSource>(
    `${BASE}/sources/${encodeURIComponent(sourceId)}/name`,
    { name },
  );
}

// ── Flows (console projection of Hive Flows) ──────────────────────────────

/**
 * ``GET /flows?page=&page_size=&status=`` — the project's flows, paginated
 * (archived rows are excluded server-side unless ``status`` asks for them).
 */
export async function fetchConsoleFlows(
  page = 1,
  pageSize = 20,
  status?: string,
): Promise<ConsoleFlowsResponse> {
  if (CONSOLE_USE_MOCKS) return mockListFlows(page, pageSize, status);
  const qs = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (status) qs.set("status", status);
  return consoleApi().get<ConsoleFlowsResponse>(`${BASE}/flows?${qs.toString()}`);
}

/** ``GET /flows/templates`` — the built-in starter-template gallery. */
export async function fetchConsoleFlowTemplates(): Promise<ConsoleFlowTemplatesResponse> {
  if (CONSOLE_USE_MOCKS) return mockFlowTemplates();
  return consoleApi().get<ConsoleFlowTemplatesResponse>(`${BASE}/flows/templates`);
}

/** ``GET /flows/{id}`` — the full flow definition (graph + activation + versioning). */
export async function fetchConsoleFlowDetail(flowId: string): Promise<ConsoleFlow> {
  if (CONSOLE_USE_MOCKS) return mockGetFlow(flowId);
  return consoleApi().get<ConsoleFlow>(`${BASE}/flows/${encodeURIComponent(flowId)}`);
}

/** ``GET /flows/{id}/stats`` — run counters over REAL runs (dry runs excluded). */
export async function fetchConsoleFlowStats(flowId: string): Promise<ConsoleFlowStats> {
  if (CONSOLE_USE_MOCKS) return mockFlowStats(flowId);
  return consoleApi().get<ConsoleFlowStats>(`${BASE}/flows/${encodeURIComponent(flowId)}/stats`);
}

/**
 * ``POST /flows/{id}/run`` — trigger a manual (or ``dry_run``) run from the
 * console. A HITL approval step parks the run as ``pending_approval`` instead
 * of failing — callers surface that state, they don't treat it as an error.
 */
export async function runConsoleFlow(
  flowId: string,
  body?: ConsoleFlowRunRequest,
): Promise<ConsoleFlowRun> {
  if (CONSOLE_USE_MOCKS) return mockRunFlow(flowId, body);
  return consoleApi().post<ConsoleFlowRun>(
    `${BASE}/flows/${encodeURIComponent(flowId)}/run`,
    body ?? {},
  );
}

export async function exportConsoleFlowRunXlsx(runId: string): Promise<Blob> {
  return consoleApi().requestBlob(`${BASE}/flows/runs/${encodeURIComponent(runId)}/export.xlsx`);
}

/**
 * ``POST /flows`` — create a flow (201, the created row). The product
 * ``FlowCreate`` shape: template cloning passes ``template_id`` + the
 * template ``graph`` and starts life as an inactive draft.
 */
export async function createConsoleFlow(payload: ConsoleFlowCreate): Promise<ConsoleFlow> {
  if (CONSOLE_USE_MOCKS) return mockCreateFlow(payload);
  return consoleApi().post<ConsoleFlow>(`${BASE}/flows`, payload);
}

/**
 * ``POST /flows/generate`` — the AI co-pilot (same copilot as the product
 * builder). The result shape is LOOSE by design (draft graph vs persisted
 * flow) — see ``ConsoleFlowGenerateResult``; callers feature-detect.
 */
export async function generateConsoleFlow(prompt: string): Promise<ConsoleFlowGenerateResult> {
  if (CONSOLE_USE_MOCKS) return mockGenerateFlow(prompt);
  return consoleApi().post<ConsoleFlowGenerateResult>(`${BASE}/flows/generate`, { prompt });
}

/** ``PATCH /flows/{id}`` — partial update (e.g. ``{is_active}`` toggle). */
export async function updateConsoleFlow(
  flowId: string,
  patch: ConsoleFlowUpdate,
): Promise<ConsoleFlow> {
  if (CONSOLE_USE_MOCKS) return mockUpdateFlow(flowId, patch);
  return consoleApi().patch<ConsoleFlow>(`${BASE}/flows/${encodeURIComponent(flowId)}`, patch);
}

/** ``DELETE /flows/{id}`` — remove the flow (and its schedule) for good. */
export async function deleteConsoleFlow(flowId: string): Promise<void> {
  if (CONSOLE_USE_MOCKS) return mockDeleteFlow(flowId);
  await consoleApi().delete<unknown>(`${BASE}/flows/${encodeURIComponent(flowId)}`);
}

// ── Flows builder surface (console-hosted editor, Aug 2026) ───────────────

/**
 * ``GET /flows/palette`` — the node catalog for the console-hosted builder.
 * Same shape as the product ``GET /api/v1/hive-flows/palette`` (the BE
 * registry is the single source of truth for both surfaces).
 */
export async function getConsoleFlowPalette(): Promise<ConsoleFlowPaletteResponse> {
  if (CONSOLE_USE_MOCKS) return mockFlowPalette();
  return consoleApi().get<ConsoleFlowPaletteResponse>(`${BASE}/flows/palette`);
}

export async function getConsoleFlowArticleGroups(flowId: string): Promise<ArticleGroupCatalog> {
  if (CONSOLE_USE_MOCKS) return {
    groups: [], total_groups: 0, ungrouped_articles: 0,
    source: "dbo.FOM_ARTI.CKY_MERC", complete: false,
  };
  return consoleApi().get<ArticleGroupCatalog>(`${BASE}/flows/${encodeURIComponent(flowId)}/article-groups`);
}

/**
 * ``GET /flows/nodes/{type}/schema`` — on-demand connector field schema for a
 * connector-backed node once provider (+ object type / sub-action) are known.
 * ``resolved:false`` ⇒ the caller keeps the node's static fields.
 */
export async function getConsoleNodeSchema(
  nodeType: string,
  params: { provider?: string; objectType?: string; subAction?: string } = {},
): Promise<ConsoleNodeSchemaResponse> {
  if (CONSOLE_USE_MOCKS) return mockNodeSchema(nodeType, params);
  const qs = new URLSearchParams();
  if (params.provider) qs.set("provider", params.provider);
  if (params.objectType) qs.set("object_type", params.objectType);
  if (params.subAction) qs.set("sub_action", params.subAction);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return consoleApi().get<ConsoleNodeSchemaResponse>(
    `${BASE}/flows/nodes/${encodeURIComponent(nodeType)}/schema${suffix}`,
  );
}

/** ``GET /flows/pending-approvals`` — runs paused on a ``logic.approval`` node. */
export async function getConsolePendingApprovals(): Promise<ConsolePendingApprovalsResponse> {
  if (CONSOLE_USE_MOCKS) return mockPendingApprovals();
  return consoleApi().get<ConsolePendingApprovalsResponse>(`${BASE}/flows/pending-approvals`);
}

/**
 * ``POST /flows/runs/{run_id}/approve`` — approve a parked run (resumes the
 * flow). Reviewer corrections ride along and overwrite the editable content
 * before the flow resumes: a bare string keeps the legacy ``editedValue``
 * call (#930), the structured form adds ``editedFields`` (G-2 — a per-field
 * record patched into the same upstream node output; wins per overlapping
 * key). Product parity: same normalization as ``@ando/core approveFlowRun``.
 */
export async function approveConsoleFlowRun(
  runId: string,
  edits?: string | ConsoleApproveRunEdits,
): Promise<ConsoleFlowRunApproveResponse> {
  const normalized: ConsoleApproveRunEdits =
    typeof edits === "string" ? { editedValue: edits } : (edits ?? {});
  if (CONSOLE_USE_MOCKS) return mockApproveFlowRun(runId, normalized.editedValue);
  return consoleApi().post<ConsoleFlowRunApproveResponse>(
    `${BASE}/flows/runs/${encodeURIComponent(runId)}/approve`,
    {
      edited_value: normalized.editedValue ?? null,
      edited_fields: normalized.editedFields ?? null,
    },
  );
}

/**
 * ``POST /flows/runs/{run_id}/reject`` — the HITL "no": terminal, the run
 * never resumes and downstream writes never fire. ``reason`` lands in the
 * run's ``approval_notes`` audit trail.
 */
export async function rejectConsoleFlowRun(
  runId: string,
  reason?: string,
): Promise<ConsoleFlowRunApproveResponse> {
  if (CONSOLE_USE_MOCKS) return mockRejectFlowRun(runId);
  return consoleApi().post<ConsoleFlowRunApproveResponse>(
    `${BASE}/flows/runs/${encodeURIComponent(runId)}/reject`,
    { reason: reason ?? null },
  );
}

/**
 * ``POST /flows/{id}/run-upload`` — start a manual run with an uploaded PDF
 * as the trigger document (multipart — same seam as the product
 * ``runFlowWithFile``): the file is persisted and the run starts with
 * ``trigger.content_item_id`` set. ``dry_run`` simulates without side effects.
 */
export async function runConsoleFlowWithUpload(
  flowId: string,
  file: File | readonly File[],
  opts: { dry_run?: boolean } = {},
): Promise<ConsoleFlowRun> {
  if (CONSOLE_USE_MOCKS) return mockRunFlowUpload(flowId, opts.dry_run ?? false);
  const form = appendRunUploadFiles(new FormData(), file);
  if (opts.dry_run !== undefined) form.append("dry_run", String(opts.dry_run));
  return consoleApi().postMultipart<ConsoleFlowRun>(
    `${BASE}/flows/${encodeURIComponent(flowId)}/run-upload`,
    form,
    { timeout: Array.isArray(file) && file.length > 1 ? 15 * 60_000 : undefined },
  );
}

/**
 * ``GET /flows/runs/{run_id}`` — full projection of one run (status, trigger,
 * accumulated ``context``, ``node_runs``, HITL approval fields) — the same
 * ``FlowRunDetailResponse`` shape as the product endpoint. Poll it while a
 * long pipeline runs or to re-hydrate a review surface.
 */
export async function fetchConsoleFlowRun(runId: string): Promise<ConsoleFlowRunDetail> {
  if (CONSOLE_USE_MOCKS) return mockGetFlowRun(runId);
  return consoleApi().get<ConsoleFlowRunDetail>(`${BASE}/flows/runs/${encodeURIComponent(runId)}`);
}

export async function sendConsoleSupportCaseReply(
  runId: string,
  body: string,
): Promise<SupportCaseReplyResult> {
  return consoleApi().post<SupportCaseReplyResult>(
    `${BASE}/flows/runs/${encodeURIComponent(runId)}/support-case/reply`,
    { body },
  );
}

/**
 * ``GET /flows/{flow_id}/runs`` — run history (most recent first, dry-runs
 * excluded), the console projection of the same ``FlowRunListResponse`` the
 * product endpoint returns. Powers the run-surface's history rail so a settled
 * result can be reopened.
 */
export async function fetchConsoleFlowRuns(
  flowId: string,
  opts: { limit?: number } = {},
): Promise<ConsoleFlowRunList> {
  if (CONSOLE_USE_MOCKS) return { runs: [] };
  const qs = opts.limit ? `?limit=${opts.limit}` : "";
  return consoleApi().get<ConsoleFlowRunList>(
    `${BASE}/flows/${encodeURIComponent(flowId)}/runs${qs}`,
  );
}

// ── Playground ────────────────────────────────────────────────────────────

export async function runConsoleQuery(input: ConsoleQueryRequest): Promise<ConsoleQueryResponse> {
  if (CONSOLE_USE_MOCKS) return mockRunQuery(input);
  return consoleApi().post<ConsoleQueryResponse>(`${BASE}/query`, input);
}

/**
 * ``GET /sources`` (console session projection) — what the current project /
 * key can query, already intersected with the resource policy. The ids are
 * exactly the concrete selectors the playground scope picker offers on top
 * of the coarse buckets.
 */
/**
 * ``GET /uks`` — TRAMA's real, materialized UKS (structure-only, display-safe):
 * the non-structured folder/source facet forest the engine navigates. An
 * un-materialized tenant comes back as ``materialized:false`` with no facets.
 */
export async function fetchConsoleUks(): Promise<ConsoleUksResponse> {
  if (CONSOLE_USE_MOCKS) return mockGetUks();
  return consoleApi().get<ConsoleUksResponse>(`${BASE}/uks`);
}

export async function fetchConsoleSources(asKeyId?: string): Promise<ConsoleSourcesResponse> {
  if (CONSOLE_USE_MOCKS) return mockListSources(asKeyId);
  const suffix = asKeyId ? `?as_key_id=${encodeURIComponent(asKeyId)}` : "";
  return consoleApi().get<ConsoleSourcesResponse>(`${BASE}/sources${suffix}`);
}

export interface ConsoleStructuredSourceCreated {
  source_id: string;
  name: string;
  connector_type: string;
  status: string;
}

export async function createConsoleDatabaseSource(input: {
  name: string;
  connector_type: string;
  credentials: Readonly<Record<string, unknown>>;
  config?: Readonly<Record<string, unknown>>;
}): Promise<ConsoleStructuredSourceCreated> {
  return consoleApi().post<ConsoleStructuredSourceCreated>(`${BASE}/sources/database`, input);
}

export async function importConsoleSpreadsheetSource(file: File): Promise<ConsoleStructuredSourceCreated> {
  const form = new FormData();
  form.append("file", file);
  return consoleApi().postMultipart<ConsoleStructuredSourceCreated>(`${BASE}/sources/spreadsheet`, form);
}

export async function testConsoleStructuredSource(sourceId: string): Promise<{ ok: boolean; error: string | null }> {
  return consoleApi().post<{ ok: boolean; error: string | null }>(`${BASE}/sources/${encodeURIComponent(sourceId)}/test`);
}

export async function discoverConsoleStructuredSource(sourceId: string): Promise<unknown> {
  return consoleApi().post<unknown>(`${BASE}/sources/${encodeURIComponent(sourceId)}/discover`);
}

export async function deleteConsoleStructuredSource(sourceId: string): Promise<void> {
  await consoleApi().delete<unknown>(`${BASE}/sources/${encodeURIComponent(sourceId)}`);
}

/**
 * ``POST /agentic/answer`` (console session projection) — the full agentic
 * stack (TRAMA retrieval + the Focus-gated text-to-SQL pass over connected
 * systems), read-only. Same ``as_key_id`` policy-preview extension as
 * ``runConsoleQuery``. A run can take tens of seconds by design.
 */
export async function runConsoleAgenticAnswer(
  input: ConsoleAgenticRequest,
  options?: { signal?: AbortSignal; waitForServerDeadline?: boolean },
): Promise<ConsoleAgenticResponse> {
  options?.signal?.throwIfAborted();
  if (CONSOLE_USE_MOCKS) return mockRunAgenticAnswer(input);
  if (input.trama_infrastructure === "lakehouse" && options?.waitForServerDeadline === true) {
    return consoleApi().post<ConsoleAgenticResponse>(`${BASE}/agentic/answer`, input,
      { timeout: null, signal: options.signal });
  }
  if (options?.signal) {
    return consoleApi().post<ConsoleAgenticResponse>(`${BASE}/agentic/answer`, input,
      { signal: options.signal });
  }
  return consoleApi().post<ConsoleAgenticResponse>(`${BASE}/agentic/answer`, input);
}

/**
 * ``POST /conversations`` (console session projection) — open a conversation
 * so subsequent ``runConsoleAgenticAnswer`` calls carry it as
 * ``conversation_id`` and answer multi-turn. Optional ``title`` (the first
 * question titles it verbatim when omitted).
 */
export async function createConsoleConversation(title?: string, options?: { signal?: AbortSignal }): Promise<ConsoleConversation> {
  options?.signal?.throwIfAborted();
  if (CONSOLE_USE_MOCKS) return mockCreateConversation(title);
  if (options?.signal) {
    return consoleApi().post<ConsoleConversation>(`${BASE}/conversations`,
      title ? { title } : undefined, { signal: options.signal });
  }
  return consoleApi().post<ConsoleConversation>(
    `${BASE}/conversations`, title ? { title } : undefined,
  );
}

export async function fetchConsoleAcquiredContent(
  documentId: string, offset = 0,
): Promise<import("./types").ConsoleAcquiredContent> {
  return consoleApi().get<import("./types").ConsoleAcquiredContent>(
    `${BASE}/documents/${encodeURIComponent(documentId)}/chunks?limit=5&offset=${offset}`,
  );
}

export async function fetchConsoleProviderAccounts(): Promise<{ accounts: import("./types").ConsoleProviderAccount[] }> {
  return consoleApi().get(`${BASE}/models/providers`);
}
export async function setConsoleProviderAccount(provider: string, body: { api_key?: string; params?: Record<string, unknown> }): Promise<unknown> {
  return consoleApi().post(`${BASE}/models/providers/${encodeURIComponent(provider)}`, body);
}
export async function deleteConsoleProviderAccount(provider: string): Promise<{ removed: boolean }> {
  return consoleApi().delete(`${BASE}/models/providers/${encodeURIComponent(provider)}`);
}

/** Advisory metadata preview. Never persists the draft or starts ingestion. */
export async function fetchConsoleIngestionEstimate(connectionId: string, draft: import("./types").ConsoleIngestionEstimateDraft, signal?: AbortSignal): Promise<import("./types").ConsoleIngestionEstimate> {
  if (CONSOLE_USE_MOCKS) return {
    estimate_id: "mock-unavailable", status: "unavailable", work_mode: "initial", currency: "USD",
    customer_amount: null, known_subtotal: null,
    quantity: { observed_items: 0, unknown_items: null, enumeration_complete: false },
    coverage: { method: "metadata", uncovered_stages: [] }, selection_hash: "mock", scope_version: "mock",
    pricing_version: "mock", pipeline_version: "mock", estimator_version: "mock",
    expires_at: new Date(Date.now() + 600_000).toISOString(), assumptions: [], warnings: [],
  };
  return consoleApi().post(`${PERSONAL_BASE}/${encodeURIComponent(connectionId)}/ingestion-estimates`, draft, { signal });
}

export interface ConsoleStructuredIngestionEstimate {
  catalog_status: string; mode: string; total_tables: number; total_columns: number;
  tables_to_profile: number; columns_to_profile: number; unchanged_tables: number;
  blocked_tables: number; baseline_review_tables: number;
  downstream_cost_included: false; currency: "USD";
  customer_cost_usd: { low: string; high: string } | null;
  warnings: string[]; commercial_policy: string;
}
export async function fetchConsoleStructuredIngestionEstimate(connectionId: string, signal?: AbortSignal): Promise<ConsoleStructuredIngestionEstimate> {
  return consoleApi().get(`${BASE}/connectors/${encodeURIComponent(connectionId)}/ingestion-estimate`, { signal });
}
